import { useState } from 'react';
import PropTypes from 'prop-types';

function toInteger (dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number)
}

var MILLISECONDS_IN_MINUTE = 60000;

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds (dirtyDate) {
  var date = new Date(dirtyDate.getTime());
  var baseTimezoneOffset = date.getTimezoneOffset();
  date.setSeconds(0, 0);
  var millisecondsPartOfTimezoneOffset = date.getTime() % MILLISECONDS_IN_MINUTE;

  return baseTimezoneOffset * MILLISECONDS_IN_MINUTE + millisecondsPartOfTimezoneOffset
}

var MILLISECONDS_IN_HOUR = 3600000;
var MILLISECONDS_IN_MINUTE$1 = 60000;
var DEFAULT_ADDITIONAL_DIGITS = 2;

var patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/
};

var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;

/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The previous `parse` implementation was renamed to `parseISO`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward
 *   parseISO('2016-01-01')
 *   ```
 *
 * - `parseISO` now validates separate date and time values in ISO-8601 strings
 *   and returns `Invalid Date` if the date is invalid.
 *
 *   ```javascript
 *   parseISO('2018-13-32')
 *   //=> Invalid Date
 *   ```
 *
 * - `parseISO` now doesn't fall back to `new Date` constructor
 *   if it fails to parse a string argument. Instead, it returns `Invalid Date`.
 *
 * @param {String} argument - the value to convert
 * @param {Object} [options] - an object with options.
 * @param {0|1|2} [options.additionalDigits=2] - the additional number of digits in the extended year format
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.additionalDigits` must be 0, 1 or 2
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * var result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * var result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */
function parseISO(argument, dirtyOptions) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var options = dirtyOptions || {};

  var additionalDigits =
    options.additionalDigits == null
      ? DEFAULT_ADDITIONAL_DIGITS
      : toInteger(options.additionalDigits);
  if (
    additionalDigits !== 2 &&
    additionalDigits !== 1 &&
    additionalDigits !== 0
  ) {
    throw new RangeError('additionalDigits must be 0, 1 or 2')
  }

  if (
    !(
      typeof argument === 'string' ||
      Object.prototype.toString.call(argument) === '[object String]'
    )
  ) {
    return new Date(NaN)
  }

  var dateStrings = splitDateString(argument);

  var date;
  if (dateStrings.date) {
    var parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate(parseYearResult.restDateString, parseYearResult.year);
  }

  if (isNaN(date) || !date) {
    return new Date(NaN)
  }

  var timestamp = date.getTime();
  var time = 0;
  var offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);
    if (isNaN(time) || time === null) {
      return new Date(NaN)
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);
    if (isNaN(offset)) {
      return new Date(NaN)
    }
  } else {
    var fullTime = timestamp + time;
    var fullTimeDate = new Date(fullTime);

    offset = getTimezoneOffsetInMilliseconds(fullTimeDate);

    // Adjust time when it's coming from DST
    var fullTimeDateNextDay = new Date(fullTime);
    fullTimeDateNextDay.setDate(fullTimeDate.getDate() + 1);
    var offsetDiff =
      getTimezoneOffsetInMilliseconds(fullTimeDateNextDay) - offset;
    if (offsetDiff > 0) {
      offset += offsetDiff;
    }
  }

  return new Date(timestamp + time + offset)
}

function splitDateString(dateString) {
  var dateStrings = {};
  var array = dateString.split(patterns.dateTimeDelimiter);
  var timeString;

  if (/:/.test(array[0])) {
    dateStrings.date = null;
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];
    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(dateStrings.date.length, dateString.length);
    }
  }

  if (timeString) {
    var token = patterns.timezone.exec(timeString);
    if (token) {
      dateStrings.time = timeString.replace(token[1], '');
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings
}

function parseYear(dateString, additionalDigits) {
  var regex = new RegExp(
    '^(?:(\\d{4}|[+-]\\d{' +
      (4 + additionalDigits) +
      '})|(\\d{2}|[+-]\\d{' +
      (2 + additionalDigits) +
      '})$)'
  );

  var captures = dateString.match(regex);
  // Invalid ISO-formatted year
  if (!captures) return { year: null }

  var year = captures[1] && parseInt(captures[1]);
  var century = captures[2] && parseInt(captures[2]);

  return {
    year: century == null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length)
  }
}

function parseDate(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return null

  var captures = dateString.match(dateRegex);
  // Invalid ISO-formatted string
  if (!captures) return null

  var isWeekDate = !!captures[4];
  var dayOfYear = parseDateUnit(captures[1]);
  var month = parseDateUnit(captures[2]) - 1;
  var day = parseDateUnit(captures[3]);
  var week = parseDateUnit(captures[4]) - 1;
  var dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN)
    }
    return dayOfISOWeekYear(year, week, dayOfWeek)
  } else {
    var date = new Date(0);
    if (
      !validateDate(year, month, day) ||
      !validateDayOfYearDate(year, dayOfYear)
    ) {
      return new Date(NaN)
    }
    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1
}

function parseTime(timeString) {
  var captures = timeString.match(timeRegex);
  if (!captures) return null // Invalid ISO-formatted time

  var hours = parseTimeUnit(captures[1]);
  var minutes = parseTimeUnit(captures[2]);
  var seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN
  }

  return (
    (hours % 24) * MILLISECONDS_IN_HOUR +
    minutes * MILLISECONDS_IN_MINUTE$1 +
    seconds * 1000
  )
}

function parseTimeUnit(value) {
  return (value && parseFloat(value.replace(',', '.'))) || 0
}

function parseTimezone(timezoneString) {
  if (timezoneString === 'Z') return 0

  var captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0

  var sign = captures[1] === '+' ? -1 : 1;
  var hours = parseInt(captures[2]);
  var minutes = (captures[3] && parseInt(captures[3])) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN
  }

  return (
    sign * (hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE$1)
  )
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  var date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  var fourthOfJanuaryDay = date.getUTCDay() || 7;
  var diff = (week || 0) * 7 + (day || 0) + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date
}

// Validation functions

// February is null to handle the leap year (using ||)
var daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || (year % 4 === 0 && year % 100)
}

function validateDate(year, month, date) {
  return !(
    month < 0 ||
    month > 11 ||
    date < 1 ||
    date > (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28))
  )
}

function validateDayOfYearDate(year, dayOfYear) {
  return !(dayOfYear < 1 || dayOfYear > (isLeapYearIndex(year) ? 366 : 365))
}

function validateWeekDate(_year, week, day) {
  return !(week < 0 || week > 52 || day < 0 || day > 6)
}

function validateTime(hours, minutes, seconds) {
  return !(
    seconds < 0 ||
    seconds >= 60 ||
    minutes < 0 ||
    minutes >= 60 ||
    hours < 0 ||
    hours >= 25
  )
}

function validateTimezone(_hours, minutes) {
  return !(minutes < 0 || minutes > 59)
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * var result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * var result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (
    argument instanceof Date ||
    (typeof argument === 'object' && argStr === '[object Date]')
  ) {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime())
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument)
  } else {
    if (
      (typeof argument === 'string' || argStr === '[object String]') &&
      typeof console !== 'undefined'
    ) {
      console.warn(
        "Starting with v2.0.0-beta.1 date-fns doesn't accept strings as arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"
      );
      console.warn(new Error().stack);
    }
    return new Date(NaN)
  }
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {*} value - the value to check
 * @returns {boolean} true if the given value is a date
 * @throws {TypeError} 1 arguments required
 *
 * @example
 * // For a valid date:
 * var result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * var result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * var result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * var result = isDate({})
 * //=> false
 */
function isDate(value) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  return (
    value instanceof Date ||
    (typeof value === 'object' &&
      Object.prototype.toString.call(value) === '[object Date]')
  )
}

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var jsx = function () {
  var REACT_ELEMENT_TYPE = typeof Symbol === "function" && Symbol.for && Symbol.for("react.element") || 0xeac7;
  return function createRawReactElement(type, props, key, children) {
    var defaultProps = type && type.defaultProps;
    var childrenLength = arguments.length - 3;

    if (!props && childrenLength !== 0) {
      props = {};
    }

    if (props && defaultProps) {
      for (var propName in defaultProps) {
        if (props[propName] === void 0) {
          props[propName] = defaultProps[propName];
        }
      }
    } else if (!props) {
      props = defaultProps || {};
    }

    if (childrenLength === 1) {
      props.children = children;
    } else if (childrenLength > 1) {
      var childArray = Array(childrenLength);

      for (var i = 0; i < childrenLength; i++) {
        childArray[i] = arguments[i + 3];
      }

      props.children = childArray;
    }

    return {
      $$typeof: REACT_ELEMENT_TYPE,
      type: type,
      key: key === undefined ? null : '' + key,
      ref: null,
      props: props,
      _owner: null
    };
  };
}();

var asyncIterator = function (iterable) {
  if (typeof Symbol === "function") {
    if (Symbol.asyncIterator) {
      var method = iterable[Symbol.asyncIterator];
      if (method != null) return method.call(iterable);
    }

    if (Symbol.iterator) {
      return iterable[Symbol.iterator]();
    }
  }

  throw new TypeError("Object is not async iterable");
};

var asyncGenerator = function () {
  function AwaitValue(value) {
    this.value = value;
  }

  function AsyncGenerator(gen) {
    var front, back;

    function send(key, arg) {
      return new Promise(function (resolve, reject) {
        var request = {
          key: key,
          arg: arg,
          resolve: resolve,
          reject: reject,
          next: null
        };

        if (back) {
          back = back.next = request;
        } else {
          front = back = request;
          resume(key, arg);
        }
      });
    }

    function resume(key, arg) {
      try {
        var result = gen[key](arg);
        var value = result.value;

        if (value instanceof AwaitValue) {
          Promise.resolve(value.value).then(function (arg) {
            resume("next", arg);
          }, function (arg) {
            resume("throw", arg);
          });
        } else {
          settle(result.done ? "return" : "normal", result.value);
        }
      } catch (err) {
        settle("throw", err);
      }
    }

    function settle(type, value) {
      switch (type) {
        case "return":
          front.resolve({
            value: value,
            done: true
          });
          break;

        case "throw":
          front.reject(value);
          break;

        default:
          front.resolve({
            value: value,
            done: false
          });
          break;
      }

      front = front.next;

      if (front) {
        resume(front.key, front.arg);
      } else {
        back = null;
      }
    }

    this._invoke = send;

    if (typeof gen.return !== "function") {
      this.return = undefined;
    }
  }

  if (typeof Symbol === "function" && Symbol.asyncIterator) {
    AsyncGenerator.prototype[Symbol.asyncIterator] = function () {
      return this;
    };
  }

  AsyncGenerator.prototype.next = function (arg) {
    return this._invoke("next", arg);
  };

  AsyncGenerator.prototype.throw = function (arg) {
    return this._invoke("throw", arg);
  };

  AsyncGenerator.prototype.return = function (arg) {
    return this._invoke("return", arg);
  };

  return {
    wrap: function (fn) {
      return function () {
        return new AsyncGenerator(fn.apply(this, arguments));
      };
    },
    await: function (value) {
      return new AwaitValue(value);
    }
  };
}();

var asyncGeneratorDelegate = function (inner, awaitWrap) {
  var iter = {},
      waiting = false;

  function pump(key, value) {
    waiting = true;
    value = new Promise(function (resolve) {
      resolve(inner[key](value));
    });
    return {
      done: false,
      value: awaitWrap(value)
    };
  }

  if (typeof Symbol === "function" && Symbol.iterator) {
    iter[Symbol.iterator] = function () {
      return this;
    };
  }

  iter.next = function (value) {
    if (waiting) {
      waiting = false;
      return value;
    }

    return pump("next", value);
  };

  if (typeof inner.throw === "function") {
    iter.throw = function (value) {
      if (waiting) {
        waiting = false;
        throw value;
      }

      return pump("throw", value);
    };
  }

  if (typeof inner.return === "function") {
    iter.return = function (value) {
      return pump("return", value);
    };
  }

  return iter;
};

var asyncToGenerator = function (fn) {
  return function () {
    var gen = fn.apply(this, arguments);
    return new Promise(function (resolve, reject) {
      function step(key, arg) {
        try {
          var info = gen[key](arg);
          var value = info.value;
        } catch (error) {
          reject(error);
          return;
        }

        if (info.done) {
          resolve(value);
        } else {
          return Promise.resolve(value).then(function (value) {
            step("next", value);
          }, function (err) {
            step("throw", err);
          });
        }
      }

      return step("next");
    });
  };
};

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var defineEnumerableProperties = function (obj, descs) {
  for (var key in descs) {
    var desc = descs[key];
    desc.configurable = desc.enumerable = true;
    if ("value" in desc) desc.writable = true;
    Object.defineProperty(obj, key, desc);
  }

  return obj;
};

var defaults = function (obj, defaults) {
  var keys = Object.getOwnPropertyNames(defaults);

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var value = Object.getOwnPropertyDescriptor(defaults, key);

    if (value && value.configurable && obj[key] === undefined) {
      Object.defineProperty(obj, key, value);
    }
  }

  return obj;
};

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var get = function get(object, property, receiver) {
  if (object === null) object = Function.prototype;
  var desc = Object.getOwnPropertyDescriptor(object, property);

  if (desc === undefined) {
    var parent = Object.getPrototypeOf(object);

    if (parent === null) {
      return undefined;
    } else {
      return get(parent, property, receiver);
    }
  } else if ("value" in desc) {
    return desc.value;
  } else {
    var getter = desc.get;

    if (getter === undefined) {
      return undefined;
    }

    return getter.call(receiver);
  }
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var _instanceof = function (left, right) {
  if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
    return right[Symbol.hasInstance](left);
  } else {
    return left instanceof right;
  }
};

var interopRequireDefault = function (obj) {
  return obj && obj.__esModule ? obj : {
    default: obj
  };
};

var interopRequireWildcard = function (obj) {
  if (obj && obj.__esModule) {
    return obj;
  } else {
    var newObj = {};

    if (obj != null) {
      for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key];
      }
    }

    newObj.default = obj;
    return newObj;
  }
};

var newArrowCheck = function (innerThis, boundThis) {
  if (innerThis !== boundThis) {
    throw new TypeError("Cannot instantiate an arrow function");
  }
};

var objectDestructuringEmpty = function (obj) {
  if (obj == null) throw new TypeError("Cannot destructure undefined");
};

var objectWithoutProperties = function (obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var selfGlobal = typeof global === "undefined" ? self : global;

var set = function set(object, property, value, receiver) {
  var desc = Object.getOwnPropertyDescriptor(object, property);

  if (desc === undefined) {
    var parent = Object.getPrototypeOf(object);

    if (parent !== null) {
      set(parent, property, value, receiver);
    }
  } else if ("value" in desc && desc.writable) {
    desc.value = value;
  } else {
    var setter = desc.set;

    if (setter !== undefined) {
      setter.call(receiver, value);
    }
  }

  return value;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var slicedToArrayLoose = function (arr, i) {
  if (Array.isArray(arr)) {
    return arr;
  } else if (Symbol.iterator in Object(arr)) {
    var _arr = [];

    for (var _iterator = arr[Symbol.iterator](), _step; !(_step = _iterator.next()).done;) {
      _arr.push(_step.value);

      if (i && _arr.length === i) break;
    }

    return _arr;
  } else {
    throw new TypeError("Invalid attempt to destructure non-iterable instance");
  }
};

var taggedTemplateLiteral = function (strings, raw) {
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
};

var taggedTemplateLiteralLoose = function (strings, raw) {
  strings.raw = raw;
  return strings;
};

var temporalRef = function (val, name, undef) {
  if (val === undef) {
    throw new ReferenceError(name + " is not defined - temporal dead zone");
  } else {
    return val;
  }
};

var temporalUndefined = {};

var toArray = function (arr) {
  return Array.isArray(arr) ? arr : Array.from(arr);
};

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

var babelHelpers = /*#__PURE__*/Object.freeze({
  jsx: jsx,
  asyncIterator: asyncIterator,
  asyncGenerator: asyncGenerator,
  asyncGeneratorDelegate: asyncGeneratorDelegate,
  asyncToGenerator: asyncToGenerator,
  classCallCheck: classCallCheck,
  createClass: createClass,
  defineEnumerableProperties: defineEnumerableProperties,
  defaults: defaults,
  defineProperty: defineProperty,
  get: get,
  inherits: inherits,
  interopRequireDefault: interopRequireDefault,
  interopRequireWildcard: interopRequireWildcard,
  newArrowCheck: newArrowCheck,
  objectDestructuringEmpty: objectDestructuringEmpty,
  objectWithoutProperties: objectWithoutProperties,
  possibleConstructorReturn: possibleConstructorReturn,
  selfGlobal: selfGlobal,
  set: set,
  slicedToArray: slicedToArray,
  slicedToArrayLoose: slicedToArrayLoose,
  taggedTemplateLiteral: taggedTemplateLiteral,
  taggedTemplateLiteralLoose: taggedTemplateLiteralLoose,
  temporalRef: temporalRef,
  temporalUndefined: temporalUndefined,
  toArray: toArray,
  toConsumableArray: toConsumableArray,
  typeof: _typeof,
  extends: _extends,
  instanceof: _instanceof
});

/**
 * Helpers to avoid importing Lodash methods.
 * Some ideas here taken from Lodash.
 */

var UNDEFINED = void 0;
var MAX_SAFE_INTEGER = 9007199254740991;

var protoToString = Object.prototype.toString;

var isUndefined = function isUndefined(v) {
	return v === UNDEFINED;
};
var isNil = function isNil(v) {
	return v === UNDEFINED || v === null;
};

function isType(v, type) {
	if (isNil(v)) return false;

	var t = typeof v === 'undefined' ? 'undefined' : _typeof(v);
	if (t === type.toLowerCase()) return true;

	return t === 'object' && protoToString.call(v) === '[object ' + type + ']';
}

var isArray = function isArray(v) {
	return Array.isArray(v);
};
var isBoolean = function isBoolean(v) {
	return v === true || v === false;
};
var isString = function isString(v) {
	return isType(v, 'String');
};
var isRegExp = function isRegExp(v) {
	return isType(v, 'RexExp');
};
var isObject = function isObject(v) {
	return v != null && (typeof v === 'undefined' ? 'undefined' : _typeof(v)) === 'object';
};
var isFunction = function isFunction(v) {
	return isType(v, 'Function');
};
var isNumber = function isNumber(v) {
	return isType(v, 'Number');
};
var isInteger = function isInteger(v) {
	return isNumber(v) && v % 1 === 0;
};
var isSafeInteger = function isSafeInteger(v) {
	return isInteger(v) && v < MAX_SAFE_INTEGER;
};

var toString = function toString(v) {
	return isNil(v) ? '' : typeof v === 'string' ? v : '' + v;
};
var toNumber = function toNumber(v) {
	return isNumber(v) ? v : v ? +v : 0;
};
var toInteger$1 = function toInteger(v) {
	return isInteger(v) ? v : Math.floor(toNumber(v));
};

var trim = function trim(v) {
	return isString(v) ? v.trim() : v;
};

var inRange = function inRange(v, v1, v2) {
	return v >= v1 && v <= v2 || v <= v1 && v >= v2;
};

var omit = function omit(o, a) {
	var keys = isArray(a) ? a : [a];
	var obj = Object.assign({}, o);
	var _iteratorNormalCompletion = true;
	var _didIteratorError = false;
	var _iteratorError = undefined;

	try {
		for (var _iterator = keys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
			var key = _step.value;
			if (key) delete obj[key];
		}
	} catch (err) {
		_didIteratorError = true;
		_iteratorError = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion && _iterator.return) {
				_iterator.return();
			}
		} finally {
			if (_didIteratorError) {
				throw _iteratorError;
			}
		}
	}

	return obj;
};

var uniq = function uniq(arr) {
	return Array.from(new Set(arr));
};

var isPlainObject = function isPlainObject(v) {
	if (!isObject(v) || protoToString.call(v) !== '[object Object]') {
		return false;
	}

	if (Object.getPrototypeOf(v) === null) {
		return true;
	}

	var proto = v;
	while (Object.getPrototypeOf(proto) !== null) {
		proto = Object.getPrototypeOf(proto);
	}
	return Object.getPrototypeOf(v) === proto;
};

var forOwn = function forOwn(o, fn) {
	return Object.keys(o).forEach(function (key) {
		return fn(o[key], key, o);
	});
};

var isEmpty = function isEmpty(v) {
	if (isNil(v)) return true;
	if (isArray(v) || isString(v)) return !v.length();
	if (isPlainObject(v)) return !Object.keys(v).length;
	if (isType(v, 'Map') || isType(v, 'Set')) return !v.size;
	return false;
};

// export const defaults = (o, d) => o
// export const defaultsDeep = (o, d) => o


var clone = function clone(v) {
	return isPlainObject(v) ? Object.assign({}, v) : isArray(v) ? v.slice(v) : v;
};

function recursiveClone(branch) {
	if (isPlainObject(branch)) {
		var obj = {};
		for (var key in branch) {
			obj[key] = recursiveClone(branch[key]);
		}
		return obj;
	}

	if (isArray(branch)) {
		var len = branch.length;
		var arr = new Array(len);
		for (var i = 0; i < len; i++) {
			arr[i] = recursiveClone(branch[i]);
		}
		return arr;
	}

	// Return any other object or simple value unchanged
	return branch;
}

var cloneDeep = function cloneDeep(obj) {
	return recursiveClone(obj);
};

function mergeBranch(target, source) {
	for (var key in source) {
		var targetVal = target[key];
		var sourceVal = source[key];

		if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
			mergeBranch(targetVal, sourceVal);
		} else if (!isUndefined(sourceVal)) {
			target[key] = sourceVal;
		}
	}
}

var merge = function merge(target) {
	for (var _len = arguments.length, sources = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
		sources[_key - 1] = arguments[_key];
	}

	if (isPlainObject(target)) {
		var _iteratorNormalCompletion2 = true;
		var _didIteratorError2 = false;
		var _iteratorError2 = undefined;

		try {
			for (var _iterator2 = sources[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
				var source = _step2.value;

				if (isPlainObject(source)) {
					mergeBranch(target, source);
				}
			}
		} catch (err) {
			_didIteratorError2 = true;
			_iteratorError2 = err;
		} finally {
			try {
				if (!_iteratorNormalCompletion2 && _iterator2.return) {
					_iterator2.return();
				}
			} finally {
				if (_didIteratorError2) {
					throw _iteratorError2;
				}
			}
		}
	}

	return target;
};

function setDefaults(source, defaults, deep) {
	if (!isPlainObject(source) || !isPlainObject(defaults)) return;

	forOwn(defaults, function (val, key) {
		var srcVal = source[key];
		var defVal = defaults[key];

		if (isUndefined(srcVal)) {
			source[key] = isPlainObject(defVal) || isArray(defVal) ? cloneDeep(defVal) : defVal;
		} else if (deep && isPlainObject(srcVal) && isPlainObject(defVal)) {
			setDefaults(srcVal, defVal, true);
		}
	});
}

var defaults$1 = function defaults(srcObj) {
	for (var _len2 = arguments.length, defObjs = Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
		defObjs[_key2 - 1] = arguments[_key2];
	}

	defObjs.forEach(function (defs) {
		return setDefaults(srcObj, defs);
	});
};

var defaultsDeep = function defaultsDeep(srcObj) {
	for (var _len3 = arguments.length, defObjs = Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
		defObjs[_key3 - 1] = arguments[_key3];
	}

	defObjs.forEach(function (defs) {
		return setDefaults(srcObj, defs, true);
	});
};

var isEqual = function isEqual(v1, v2) {
	var simpleValue = function simpleValue(val) {
		// Undefined and Null are equal to "" for data comparison.
		if (isNil(val)) return '';

		// Stringify Array and Hash values for simple comparison; rarely needed.
		if (isPlainObject(val) || isArray(val)) return JSON.stringify(val);

		// Convert data objects to number for comparison
		if (isDate(val)) return val.getTime();

		return val;
	};
	return simpleValue(v1) === simpleValue(v2);
};

var utils = /*#__PURE__*/Object.freeze({
  isDate: isDate,
  isUndefined: isUndefined,
  isNil: isNil,
  isArray: isArray,
  isBoolean: isBoolean,
  isString: isString,
  isRegExp: isRegExp,
  isObject: isObject,
  isFunction: isFunction,
  isNumber: isNumber,
  isInteger: isInteger,
  isSafeInteger: isSafeInteger,
  toString: toString,
  toNumber: toNumber,
  toInteger: toInteger$1,
  trim: trim,
  inRange: inRange,
  omit: omit,
  uniq: uniq,
  isPlainObject: isPlainObject,
  forOwn: forOwn,
  isEmpty: isEmpty,
  clone: clone,
  cloneDeep: cloneDeep,
  merge: merge,
  defaults: defaults$1,
  defaultsDeep: defaultsDeep,
  isEqual: isEqual
});

// Match timestamps  with no date portion, like: "08:30" or "14:00:00"
var reTimeOnly = /^[\d]{2}:[\d]{2}[\d:]*$/;
var fixTimestamp = function fixTimestamp(date) {
  return isString(date) && reTimeOnly.test(date) ? '1970-01-01T' + date : date;
};

/**
 * PUBLIC UTILITY to create Date object, AND handle time-only strings
 *
 * @public
 * @param {(string|number|Object)} date
 * @returns {Date|*}
 */
var parseDate$1 = function parseDate(date) {
  return isDate(date) ? date : isString(date) ? parseISO(fixTimestamp(date)) : toDate(date);
};

/**
 * Helper methods used by FormManager; exported as an object of methods
 */

function itemToArray(item) {
	// Avoid creating an array with just an empty string or other falsey value
	if (!item) return [];

	// ARRAY already - ASSUME has no nested arrays
	if (isArray(item)) return item;

	// HASH value; like a field-errors object
	if (isPlainObject(item)) {
		var arrItems = []; // init, may change

		forOwn(item, function (value) {
			if (value && value.length) {
				if (isArray(value)) {
					arrItems = arrItems.concat(value);
				} else {
					arrItems.push(value);
				}
			}
		});

		return arrItems;
	}

	// STRING or OTHER item
	return [item];
}

/**
 * Change all empty-string and undefined values to Null.
 * Mutate the original object - caller should clone first if necessary.
 *
 * @param {Object} data
 */
var emptyValuesToNull = function emptyValuesToNull(data) {
	forOwn(data, function (value, key) {
		if (value === '' || value === undefined) {
			data[key] = null;
		}
	});
};

/**
 * Convert a fieldName into an array of keys - may only be one key.
 * NOT exported - helper for get/setObjectValue methods
 *
 * @param {string} path
 */
function pathToKeysArray(path) {
	// Dots delimit an 'object path'
	return isArray(path) ? path : path.split('.');
}

/**
 *
 * @param {Object} hash		Object to modify
 * @param {string} path		String-path like 'data/who/gender'
 * @param {Object} [opts]   Configuration options
 * @returns {*}				Value at specified path, or undefined if not found
 */
function getObjectValue(hash, path, opts) {
	if (!hash || !isPlainObject(hash)) return undefined;

	var getOpts = Object.assign({ cloneValue: false, deepClone: true }, opts);
	var branch = hash;

	// If a path was passed, trace the path inside state.form
	if (path && path !== '/') {
		// Slash(es) in the path indicate that we should recurse downward
		var keys = pathToKeysArray(path);
		var _iteratorNormalCompletion = true;
		var _didIteratorError = false;
		var _iteratorError = undefined;

		try {
			for (var _iterator = keys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
				var key = _step.value;

				// If branch is not an object, then cannot recurse; abort
				if (!isPlainObject(branch)) return undefined;

				branch = branch[key];

				// If requested key not found, abort
				if (isUndefined(branch)) return undefined;
			}
		} catch (err) {
			_didIteratorError = true;
			_iteratorError = err;
		} finally {
			try {
				if (!_iteratorNormalCompletion && _iterator.return) {
					_iterator.return();
				}
			} finally {
				if (_didIteratorError) {
					throw _iteratorError;
				}
			}
		}
	}

	return !getOpts.cloneValue ? branch : getOpts.deepClone ? cloneDeep(branch) : clone(branch);
}

/**
 *
 * @param {Object} hash		Object to modify
 * @param {string} path		String-path like 'data/who/gender'
 * @param {*} value			Value - could be anything!
 * @param {Object} [opts]   Configuration
 * @returns {boolean}		True if value set; false if value is unchanged
 */
function setObjectValue(hash, path, value, opts) {
	if (!hash || !isPlainObject(hash)) return undefined;

	var setOpts = Object.assign({ cloneValue: false, merge: false }, opts);

	// If a path was passed, recurse into the object
	if (path && path !== '/') {
		var keys = pathToKeysArray(path);
		var lastIdx = keys.length - 1;
		var lastKey = keys[lastIdx];
		var branch = hash;

		// Recurse into data hash for name-paths like 'who/location/address1'
		for (var idx = 0; idx < lastIdx; idx++) {
			var key = keys[idx];
			var branchValue = branch[key];

			// Create a branch (hash) here if it doesn't exist yet
			// OVERWRITE any non-object value because path specifies it!
			if (!isPlainObject(branchValue)) {
				branch[key] = {};
			}

			// Update branch for next loop
			branch = branch[key];
		}

		var oldValue = branch[lastKey];

		// Check whether value has changed - ignore objects, assume changed
		if (!isObject(value) && value === oldValue) {
			return false; // Value was NOT updated
		}

		// If passed value is undefined, then DELETE the specified path
		if (value === undefined) {
			delete branch[lastKey]; // OK if key does not exist
			return !isUndefined(oldValue); // True IF a value existed before
		}

		// Write the passed value at end of the path (last branch)
		// Ignore any existing value - we do not merge data here.
		if (setOpts.merge && isPlainObject(branch[lastKey]) && isPlainObject(value)) {
			merge(branch[lastKey], setOpts.cloneValue ? clone(value) : value);
		}
		// Set value at path specified; clone value to break byRef
		else {
				branch[lastKey] = setOpts.cloneValue ? clone(value) : value;
			}

		return true; // Value was updated
	} else if (isPlainObject(path)) {
		// The path is an object (key/value pairs) to merge into hash-root
		merge(hash, setOpts.cloneValue ? cloneDeep(path) : path);
		return true; // Values were updated
	} else {
		console.warn('FormManager: No path specified to set value: "' + value + '"');
		return false; // Value was NOT updated
	}
}

window.nodash = utils;

/**
 * FormManager sub-component to handle form-field data
 *
 * @param {Object} formManager        FormManager instance object
 * @param {Object} components            Hash of FormManager sub-components
 * @param {Object} components.config    FormManagerConfig instance object
 * @param {Object} components.state    FormManagerState instance object
 * @returns {Object}                    Config API for this instance
 * @constructor
 */
function Data(formManager, components) {
	// Auto-instantiate so 'new' keyword is NOT required
	// (It's best practice to not require 'new' as it's an internal design)
	if (!(this instanceof Data)) {
		return new Data(formManager, components);
	}

	var config = components.config,
	    state = components.state,
	    internal = components.internal;

	// Extract helper methods for brevity

	var fireEventCallback = internal.fireEventCallback,
	    triggerComponentUpdate = internal.triggerComponentUpdate;
	var withFieldDefaults = config.withFieldDefaults,
	    aliasToRealName = config.aliasToRealName;
	// Create local aliases for config objects

	var formatters = config.get('formatters');
	var converters = config.get('converters');

	// DATA CACHES - both server and form versions of data
	var stateOfForm = {};
	var stateOfData = {};

	var stateOfInitialData = {};
	var dirtyFields = new Set();

	return {
		// Methods used internally by FormManager components
		init: init,
		get: getData, // GETTER for stateOfData
		set: setData, // SETTER for stateOfData
		getValue: getValue, // GETTER for one or more field-values
		setValue: setValue, // SETTER for one or more field-values
		cleanValue: cleanValue, // GETTER/PROCESSOR for one field

		// Methods exposed in FormManager API
		publicAPI: {
			isDirty: isDirty, // Has ANY value in data changed?
			isClean: isClean, // Has NO values in data changed?
			getChanges: getChanges, // Return hash containing only changed data
			getData: getData, // GETTER for stateOfData
			setData: setData, // SETTER for stateOfData
			getValue: getValue, // GETTER for one or more field-values
			setValue: setValue, // SETTER for one or more field-values
			cleanField: cleanField, // SETTER for one field
			// ALIASES
			data: getData,
			value: getValue,
			values: getValue,
			changes: getChanges,
			getValues: getValue,
			setValues: setValue,
			cleanFieldValue: cleanField
		}

		/**
   * Initialize form data, state and errors using only the config-options
   *
   * @param {(undefined|Object)} [initialData]
   * @param {(undefined|Object)} [data]
   */
	};function init(initialData, data) {
		stateOfData = {}; // In case called for a RESET
		if (initialData) setData(initialData);
		if (data) setData(data);
	}

	/**
  * PUBLIC GETTER for a specific field value.
  *
  * @public
  * @param {string} name         Field-name or alias-name
  * @param {Object} [opts]    Options
  */
	function getValue(name) {
		var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

		if (!name) {
			return cloneDeep(stateOfForm);
		}

		var getOpts = Object.assign({
			cleanValue: false,
			refresh: false
		}, opts);

		var fieldName = aliasToRealName(name);
		var fieldConfig = config.getField(fieldName) || {};

		// NOTE: stateOfForm is ONE LEVEL; no nested fields
		var value = stateOfForm[fieldName];

		// Init or refresh the cached field-value in stateOfForm
		if (opts.refresh || isUndefined(value)) {
			value = setFormValueFromData(fieldName);
		}

		// Clean field-value - NOT cached!
		if (getOpts.cleanValue) {
			value = cleanValue(value, fieldConfig);
		}

		return value;
	}

	/**
  * PUBLIC SETTER for a specific field value.
  *
  * @public
  * @param {string} name        Field-name or alias-name
  * @param {*} [value]        Any value OR opts in multi-field mode
  * @param {Object} [opts]    Options
  * @returns {Object}            All SETTERS return instance for chaining
  */
	function setValue(name, value) {
		var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
		var update = opts.update,
		    event = opts.event,
		    validate = opts.validate,
		    isInitialValue = opts.isInitialValue;

		var validationPromises = [];

		var set = function set(n, v) {
			var val = v;

			var fieldName = aliasToRealName(n);
			var fieldConfig = config.getField(fieldName);

			// Field may NOT have a configuration...
			if (fieldConfig) {
				if (fieldConfig.isData) {
					// Clean when re/initializing a value from raw data
					// Option passed by setData() IF field.cleanOnBlur = true
					if (opts.cleanValue) {
						val = cleanValue(val, fieldConfig);
					}

					setDataValue(fieldName, val, fieldConfig, isInitialValue);
				} else {
					state.set(name, val);
				}

				// Update the stateOfForm cache
				setFormValueFromData(fieldName, val);
			} else {
				setDataValue(n, value, null, isInitialValue);

				// Update the stateOfForm cache
				setFormValueFromData(n, value);
			}

			// If triggered by a field-event, validate BEFORE firing callbacks
			if (event || validate) {
				var validationEvent = validate ? 'validate' : event;
				var onChangeForm = config.get('onChange');
				var onChangeField = (config.getField(name) || {}).onChange;

				if (fieldConfig) {
					var opts2 = { update: false

						// Track promises so can wait until ALL done before 'update'
					};validationPromises.push(
					// A validation event MAY trigger validation & callback.
					// Validation MAY run, depending on event-type.
					// Validation is async so always returns a promise.
					formManager.validate(name, val, validationEvent, opts2).then(function () {
						// If field value was changed, then fire events
						// noinspection JSIncompatibleTypesComparison
						if (event === 'change') {
							fireEventCallback(onChangeField, value, name);
							fireEventCallback(onChangeForm, value, name);
						}
					}));
				} else {
					// noinspection JSIncompatibleTypesComparison
					if (event === 'change') {
						// Just fire form-level onChange, if one exists
						fireEventCallback(onChangeForm, value, name);
					}
				}
			}
		};

		// PROCESS METHOD ARGUMENTS - SET ONE -OR- ALL FIELDS?
		if (isString(name)) {
			set(name, value);
		} else if (isPlainObject(name)) {
			// Handle data-hash syntax: value(data, opts)
			forOwn(name, function (v, n) {
				set(n, v);
			});
		}

		// Note: opts.update default is true; must pass false to prevent update
		if (update !== false) {
			// If field(s) is validating, wait for that to complete
			if (validationPromises.length) {
				Promise.all(validationPromises).then(triggerComponentUpdate);
			} else {
				triggerComponentUpdate();
			}
		}

		return formManager;
	}

	/**
  * Helper to parse a field-value from data-value
  *
  * @param {string} fieldName
  * @param {*} [newDataValue]
  */
	function setFormValueFromData(fieldName, newDataValue) {
		var fieldConfig = config.getField(fieldName) || {};
		var isData = fieldConfig.isData,
		    valueFormat = fieldConfig.valueFormat,
		    valueType = fieldConfig.valueType;


		var value = !isUndefined(newDataValue) ? newDataValue : isData ? getObjectValue(stateOfData, fieldName, { clone: true, deep: false }) : state.get(fieldName);

		value = convertDataType(value, valueType);
		value = formatValue(value, valueFormat);
		value = undefinedToDefaultValue(value); // , dataType

		// Update stateOfForm cache
		// NOTE: stateOfForm is ONE LEVEL; no nested fields
		stateOfForm[fieldName] = clone(value);

		// return value to getFieldValue()
		return value;
	}

	function cleanValue(value, fldCfg) {
		if (!value) return value;
		var val = value;

		// The formatter may change the data-type, like number -> string
		var reformat = withFieldDefaults(fldCfg, 'cleaning.reformat');
		if (reformat) val = formatValue(val, reformat);

		// Only string values can be trimmed
		if (isString(value)) {
			var trimOuter = withFieldDefaults(fldCfg, 'cleaning.trim');
			var trimInner = withFieldDefaults(fldCfg, 'cleaning.trimInner');
			if (trimOuter) val = val.trim();
			if (trimInner) val = val.replace(/\s+/g, ' ');
		}

		return val;
	}

	function cleanField(name) {
		var fieldName = aliasToRealName(name);
		var fieldConfig = config.getField(fieldName);

		// Field may NOT have a configuration, or could be a 'state' value
		if (fieldConfig && fieldConfig.isData) {
			var curValue = getValue(fieldName);
			var newValue = cleanValue(curValue, fieldConfig);

			if (newValue !== curValue) {
				// Pass 'change' event so will fire event with new value
				setValue(fieldName, newValue, { event: 'change' });
			}
		}

		return formManager;
	}

	/**
  * @param {(string|Object)} [name]  Fieldname
  * @param {Object} [opts]           Options
  * @returns {(string|Object)}       Data value(s) for one-field or all-data
  */
	function getData(name, opts) {
		var namePassed = isString(name);
		var getOpts = Object.assign({ cloneValue: true }, namePassed ? opts : name);
		return namePassed ? getObjectValue(stateOfData, aliasToRealName(name), getOpts) : cloneDeep(stateOfData);
	}

	/**
  * @param {(string|Object)} [nameOrData]  Fieldname OR Data hash
  * @param {*} [fieldData]            Data for a single field
  * @returns {Object}                All SETTERS return instance for chaining
  */
	function setData(nameOrData, fieldData) {
		// Subroutine to handle recursively processing nested data paths
		var processDataBranch = function processDataBranch(parentPath, branch) {
			if (isPlainObject(branch) && !isEmpty(branch)) {
				forOwn(branch, function (value, name) {
					var fieldName = parentPath ? parentPath + '.' + name : name;
					var fieldConfig = config.getField(fieldName);

					// ASSUME a non-configured fieldname/path that contains a
					//	hash is part of the data structure, NOT a 'hash-value'
					if (!fieldConfig && isPlainObject(value)) {
						// RECURSE into object at this key-value
						processDataBranch(fieldName, value);
					}

					// Not all fields need to have a configuration
					else if (!fieldConfig || fieldConfig.isData) {
							// If cleanOnBlur = true, ALSO means clean on init-value
							var clean = withFieldDefaults(fieldConfig, 'cleaning.cleanOnBlur');
							var setOpts = { isInitialValue: true };
							if (clean) setOpts.cleanValue = true;

							setValue(fieldName, value, setOpts);
						}

						// This catches everything else - criteria just for clarity
						else if (!fieldConfig.isData) {
								state.set(fieldName, value, { update: false });
							}
				});
			}
		};

		if (isString(nameOrData)) {
			var fieldName = aliasToRealName(nameOrData);
			processDataBranch('', defineProperty({}, fieldName, fieldData));
		} else if (isPlainObject(nameOrData)) {
			processDataBranch('', nameOrData);
		} else {
			// BAD DATA PASSED - ABORT
			console.warn('Invalid data passed to FormManager.setData()');
			return;
		}

		triggerComponentUpdate();

		return formManager;
	}

	function setDataValue(fieldName, value, fieldConfig, isInitialValue) {
		var val = value;

		if (fieldConfig) {
			// dataFormat
			val = convertDataType(val, fieldConfig.dataType);
			val = formatValue(val, fieldConfig.dataFormat);
		}

		setObjectValue(stateOfData, fieldName, val);

		if (isInitialValue) {
			setObjectValue(stateOfInitialData, fieldName, val);
		}

		setIsDirty(fieldName, val);
	}

	function getChanges() {
		var changes = {};

		dirtyFields.forEach(function (fieldName) {
			var value = getObjectValue(stateOfData, fieldName, { cloneValue: true });
			setObjectValue(changes, fieldName, value);
		});

		return changes;
	}

	/**
  * @param {string} fieldName
  * @param {*} value
  */
	function setIsDirty(fieldName, value) {
		var originalValue = getObjectValue(stateOfInitialData, fieldName);
		var isOriginalValue = isUndefined(originalValue)
		// If no initial value was define, consider the value falsey
		? value === '' || value === null || value === false : isEqual(value, originalValue);

		if (isOriginalValue) {
			dirtyFields.delete(fieldName);
		} else {
			dirtyFields.add(fieldName);
		}
	}

	/**
  * @param {string } [name]
  * @returns {boolean}
  */
	function isDirty(name) {
		if (name) {
			var fieldName = aliasToRealName(name);
			return fieldName ? !!dirtyFields.has(fieldName) : false;
		} else {
			return !!dirtyFields.size;
		}
	}

	/**
  * @param {string } [name]
  * @returns {boolean}
  */
	function isClean(name) {
		return !isDirty(name);
	}

	/**
  * @param {*} value
  * @param {(string|Function)} format
  * @returns {*}
  */
	function formatValue(value, format) {
		if (!format) return value;

		if (isFunction(format)) {
			return format(value);
		}

		var fmt = format;
		var fmtOpts = undefined;
		if (isArray(format)) {
			fmt = format[0];
			fmtOpts = format[1];
		}

		// Format value if a formatter is specified
		if (fmt && formatters[fmt]) {
			return formatters[fmt](value, fmtOpts);
		}

		return value;
	}

	/**
  * @param {*} value
  * @param {string} dataType
  * @returns {*}
  */
	function convertDataType(value, dataType) {
		if (!dataType) return value;

		if (isFunction(dataType)) {
			return dataType(value);
		}

		var type = dataType;
		var opts = undefined;
		if (isArray(dataType)) {
			type = dataType[0];
			opts = dataType[1];
		}

		// Convert value if a converter is specified
		if (type && converters[type]) {
			return converters[type](value, opts);
		}

		return value;
	}

	/**
  * @param {*} value
  // * @param {(string|Array|Function)} dataType
  * @returns {*}
  */
	function undefinedToDefaultValue(value) {
		// If a non-nil value is specified, use it
		return isNil(value) ? '' : value;
	}
}

var defaultFormState = {}
// TODO: need to track load-values so know if current value is the same
// isDirty:		false,	// true if ANY field has changed
// hasErrors:	false,	// true if ANY field has an error
// Standard FormControl state keys
// dirty:		false,
// adornedStart:	false,


/**
 * FormManager sub-component to handle form-field data
 *
 * @param {Object} formManager  	FormManager instance object
 * @param {Object} components		Hash of FormManager sub-components
 * @returns {Object}             	Config API for this instance
 * @constructor
 */
;function State(formManager, components) {
	// Auto-instantiate so 'new' keyword is NOT required
	// (It's best practice to not require 'new' as it's an internal design)
	if (!(this instanceof State)) {
		return new State(formManager, components);
	}

	var internal = components.internal;

	// Extract helper methods for brevity

	var triggerComponentUpdate = internal.triggerComponentUpdate;

	// Form-State cache - data accessible only via config methods

	var stateOfForm = cloneDeep(defaultFormState);

	return {
		init: init,
		get: getState,
		set: setState,

		// Methods exposed in FormManager API
		publicAPI: {
			setState: setState, // SETTER
			getState: getState, // GETTER
			// ALIASES
			state: getState
		}

		/**
   * Init form state - also aliased as reset()
   *
   * @param {Object} [initialState]
   */
	};function init() {
		var initialState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

		stateOfForm = cloneDeep(defaultFormState);
		merge(stateOfForm, initialState);
	}

	/**
  * PUBLIC GETTER for values in the ROOT of state
  *
  * @public
  * @param {string} [key]		Path in state to return
  * @param {Object} opts        	Clone-value option
  * @returns {*}                	All form-state OR just specific key requested
  */
	function getState(key) {
		var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

		var getOpts = Object.assign({ cloneValue: true }, opts);
		// If a key is passed, then return value for just that, if exists
		if (key) return getObjectValue(stateOfForm, key, getOpts);

		// Return a deep clone to ensure state cannot be mutated
		return cloneDeep(stateOfForm);
	}

	/**
  * PUBLIC SETTER for values in the ROOT of state
  *
  * @public
  * @param {(Object|string)} key  	Path OR a hash of data to set
  * @param {*} [value]               Value to SET, if key is a string
  * @param {Object} [opts]           Options, like { cloneValue: false }
  */
	function setState(key, value) {
		var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

		var setOpts = Object.assign({ cloneValue: true, update: true }, opts);
		setObjectValue(stateOfForm, key, value, setOpts);

		if (setOpts.update) triggerComponentUpdate();
	}
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Now `isValid` doesn't throw an exception
 *   if the first argument is not an instance of Date.
 *   Instead, argument is converted beforehand using `toDate`.
 *
 *   Examples:
 *
 *   | `isValid` argument        | Before v2.0.0 | v2.0.0 onward |
 *   |---------------------------|---------------|---------------|
 *   | `new Date()`              | `true`        | `true`        |
 *   | `new Date('2016-01-01')`  | `true`        | `true`        |
 *   | `new Date('')`            | `false`       | `false`       |
 *   | `new Date(1488370835081)` | `true`        | `true`        |
 *   | `new Date(NaN)`           | `false`       | `false`       |
 *   | `'2016-01-01'`            | `TypeError`   | `true`        |
 *   | `''`                      | `TypeError`   | `false`       |
 *   | `1488370835081`           | `TypeError`   | `true`        |
 *   | `NaN`                     | `TypeError`   | `false`       |
 *
 *   We introduce this change to make *date-fns* consistent with ECMAScript behavior
 *   that try to coerce arguments to the expected type
 *   (which is also the case with other *date-fns* functions).
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * var result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * var result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * var result = isValid(new Date(''))
 * //=> false
 */
function isValid(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  return !isNaN(date)
}

/**
 * @name isEqual
 * @category Common Helpers
 * @summary Are the given dates equal?
 *
 * @description
 * Are the given dates equal?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to compare
 * @param {Date|Number} dateRight - the second date to compare
 * @returns {Boolean} the dates are equal
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 2 July 2014 06:30:45.000 and 2 July 2014 06:30:45.500 equal?
 * var result = isEqual(
 *   new Date(2014, 6, 2, 6, 30, 45, 0),
 *   new Date(2014, 6, 2, 6, 30, 45, 500)
 * )
 * //=> false
 */
function isEqual$1(dirtyLeftDate, dirtyRightDate) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var dateLeft = toDate(dirtyLeftDate);
  var dateRight = toDate(dirtyRightDate);
  return dateLeft.getTime() === dateRight.getTime()
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * var result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day?
 *
 * @description
 * Are the given dates in the same day?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 */
function isSameDay(dirtyDateLeft, dirtyDateRight) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);

  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime()
}

/**
 * @name isAfter
 * @category Common Helpers
 * @summary Is the first date after the second one?
 *
 * @description
 * Is the first date after the second one?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date that should be after the other one to return true
 * @param {Date|Number} dateToCompare - the date to compare with
 * @returns {Boolean} the first date is after the second date
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Is 10 July 1989 after 11 February 1987?
 * var result = isAfter(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> true
 */
function isAfter(dirtyDate, dirtyDateToCompare) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var dateToCompare = toDate(dirtyDateToCompare);
  return date.getTime() > dateToCompare.getTime()
}

/**
 * @name isBefore
 * @category Common Helpers
 * @summary Is the first date before the second one?
 *
 * @description
 * Is the first date before the second one?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date that should be before the other one to return true
 * @param {Date|Number} dateToCompare - the date to compare with
 * @returns {Boolean} the first date is before the second date
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Is 10 July 1989 before 11 February 1987?
 * var result = isBefore(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> false
 */
function isBefore(dirtyDate, dirtyDateToCompare) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var dateToCompare = toDate(dirtyDateToCompare);
  return date.getTime() < dateToCompare.getTime()
}

/**
 * @name isWithinInterval
 * @category Interval Helpers
 * @summary Is the given date within the interval?
 *
 * @description
 * Is the given date within the interval?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The function was renamed from `isWithinRange` to `isWithinInterval`.
 *   This change was made to mirror the use of the word "interval" in standard ISO 8601:2004 terminology:
 *
 *   ```
 *   2.1.3
 *   time interval
 *   part of the time axis limited by two instants
 *   ```
 *
 *   Also, this function now accepts an object with `start` and `end` properties
 *   instead of two arguments as an interval.
 *   This function now throws `RangeError` if the start of the interval is after its end
 *   or if any date in the interval is `Invalid Date`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *
 *   isWithinRange(
 *     new Date(2014, 0, 3),
 *     new Date(2014, 0, 1), new Date(2014, 0, 7)
 *   )
 *
 *   // v2.0.0 onward
 *
 *   isWithinInterval(
 *     new Date(2014, 0, 3),
 *     { start: new Date(2014, 0, 1), end: new Date(2014, 0, 7) }
 *   )
 *   ```
 *
 * @param {Date|Number} date - the date to check
 * @param {Interval} interval - the interval to check
 * @returns {Boolean} the date is within the interval
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} The start of an interval cannot be after its end
 * @throws {RangeError} Date in interval cannot be `Invalid Date`
 *
 * @example
 * // For the date within the interval:
 * isWithinInterval(new Date(2014, 0, 3), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> true
 *
 * @example
 * // For the date outside of the interval:
 * isWithinInterval(new Date(2014, 0, 10), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> false
 */
function isWithinInterval(dirtyDate, dirtyInterval) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var interval = dirtyInterval || {};
  var time = toDate(dirtyDate).getTime();
  var startTime = toDate(interval.start).getTime();
  var endTime = toDate(interval.end).getTime();

  // Throw an exception if start date is after end date or if any date is `Invalid Date`
  if (!(startTime <= endTime)) {
    throw new RangeError('Invalid interval')
  }

  return time >= startTime && time <= endTime
}

/**
 * @name setDayOfYear
 * @category Day Helpers
 * @summary Set the day of the year to the given date.
 *
 * @description
 * Set the day of the year to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} dayOfYear - the day of the year of the new date
 * @returns {Date} the new date with the day of the year set
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Set the 2nd day of the year to 2 July 2014:
 * var result = setDayOfYear(new Date(2014, 6, 2), 2)
 * //=> Thu Jan 02 2014 00:00:00
 */
function setDayOfYear(dirtyDate, dirtyDayOfYear) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var dayOfYear = toInteger(dirtyDayOfYear);
  date.setMonth(0);
  date.setDate(dayOfYear);
  return date
}

/**
 * @name setYear
 * @category Year Helpers
 * @summary Set the year to the given date.
 *
 * @description
 * Set the year to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} year - the year of the new date
 * @returns {Date} the new date with the year set
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Set year 2013 to 1 September 2014:
 * var result = setYear(new Date(2014, 8, 1), 2013)
 * //=> Sun Sep 01 2013 00:00:00
 */
function setYear(dirtyDate, dirtyYear) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var year = toInteger(dirtyYear);

  // Check if date is Invalid Date because Date.prototype.setFullYear ignores the value of Invalid Date
  if (isNaN(date)) {
    return new Date(NaN)
  }

  date.setFullYear(year);
  return date
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },

  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },

  halfAMinute: 'half a minute',

  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },

  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },

  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },

  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },

  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },

  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },

  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },

  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },

  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },

  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },

  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};

function formatDistance (token, count, options) {
  options = options || {};

  var result;
  if (typeof formatDistanceLocale[token] === 'string') {
    result = formatDistanceLocale[token];
  } else if (count === 1) {
    result = formatDistanceLocale[token].one;
  } else {
    result = formatDistanceLocale[token].other.replace('{{count}}', count);
  }

  if (options.addSuffix) {
    if (options.comparison > 0) {
      return 'in ' + result
    } else {
      return result + ' ago'
    }
  }

  return result
}

function buildFormatLongFn (args) {
  return function (dirtyOptions) {
    var options = dirtyOptions || {};
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format
  }
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};

var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};

var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};

var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),

  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),

  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};

function formatRelative (token, date, baseDate, options) {
  return formatRelativeLocale[token]
}

function buildLocalizeFn(args) {
  return function(dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};

    var context = options.context ? String(options.context) : 'standalone';

    var valuesArray;
    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray =
        args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var defaultWidth = args.defaultWidth;
      var width = options.width ? String(options.width) : args.defaultWidth;
      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    var index = args.argumentCallback
      ? args.argumentCallback(dirtyIndex)
      : dirtyIndex;
    return valuesArray[index]
  }
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};

var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};

var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

function ordinalNumber (dirtyNumber, dirtyOptions) {
  var number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`:
  //
  //   var options = dirtyOptions || {}
  //   var unit = String(options.unit)
  //
  // where `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'

  var rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st'
      case 2:
        return number + 'nd'
      case 3:
        return number + 'rd'
    }
  }
  return number + 'th'
}

var localize = {
  ordinalNumber: ordinalNumber,

  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),

  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return Number(quarter) - 1
    }
  }),

  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),

  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),

  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};

function buildMatchPatternFn (args) {
  return function (dirtyString, dirtyOptions) {
    var string = String(dirtyString);
    var options = dirtyOptions || {};

    var matchResult = string.match(args.matchPattern);
    if (!matchResult) {
      return null
    }
    var matchedString = matchResult[0];

    var parseResult = string.match(args.parsePattern);
    if (!parseResult) {
      return null
    }
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;

    return {
      value: value,
      rest: string.slice(matchedString.length)
    }
  }
}

function buildMatchFn (args) {
  return function (dirtyString, dirtyOptions) {
    var string = String(dirtyString);
    var options = dirtyOptions || {};
    var width = options.width;

    var matchPattern = (width && args.matchPatterns[width]) || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null
    }
    var matchedString = matchResult[0];

    var parsePatterns = (width && args.parsePatterns[width]) || args.parsePatterns[args.defaultParseWidth];

    var value;
    if (Object.prototype.toString.call(parsePatterns) === '[object Array]') {
      value = parsePatterns.findIndex(function (pattern) {
        return pattern.test(string)
      });
    } else {
      value = findKey(parsePatterns, function (pattern) {
        return pattern.test(string)
      });
    }

    value = args.valueCallback ? args.valueCallback(value) : value;
    value = options.valueCallback ? options.valueCallback(value) : value;

    return {
      value: value,
      rest: string.slice(matchedString.length)
    }
  }
}

function findKey (object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key
    }
  }
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;

var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};

var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};

var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};

var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};

var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};

var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10)
    }
  }),

  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),

  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1
    }
  }),

  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),

  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),

  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1
  }
};

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * var result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */
function addMilliseconds(dirtyDate, dirtyAmount) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount)
}

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * var result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */
function subMilliseconds(dirtyDate, dirtyAmount) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount)
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();
  while (output.length < targetLength) {
    output = '0' + output;
  }
  return sign + output
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters = {
  // Year
  y: function(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    var signedYear = date.getUTCFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length)
  },

  // Month
  M: function(date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2)
  },

  // Day of the month
  d: function(date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length)
  },

  // AM or PM
  a: function(date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
      case 'aaa':
        return dayPeriodEnumValue.toUpperCase()
      case 'aaaaa':
        return dayPeriodEnumValue[0]
      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.'
    }
  },

  // Hour [1-12]
  h: function(date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length)
  },

  // Hour [0-23]
  H: function(date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length)
  },

  // Minute
  m: function(date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length)
  },

  // Second
  s: function(date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length)
  },

  // Fraction of second
  S: function(date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(
      milliseconds * Math.pow(10, numberOfDigits - 3)
    );
    return addLeadingZeros(fractionalSeconds, token.length)
  }
};

var MILLISECONDS_IN_DAY = 86400000;

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function getUTCDayOfYear(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY) + 1
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function startOfUTCISOWeek(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var weekStartsOn = 1;

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function getUTCISOWeekYear(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();

  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);

  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year
  } else {
    return year - 1
  }
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function startOfUTCISOWeekYear(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date
}

var MILLISECONDS_IN_WEEK = 604800000;

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function getUTCISOWeek(dirtyDate) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var diff =
    startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function startOfUTCWeek(dirtyDate, dirtyOptions) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn =
    locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn =
    localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn =
    options.weekStartsOn == null
      ? defaultWeekStartsOn
      : toInteger(options.weekStartsOn);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively')
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function getUTCWeekYear (dirtyDate, dirtyOptions) {
  if (arguments.length < 1) {
    throw new TypeError('1 argument required, but only ' + arguments.length + ' present')
  }

  var date = toDate(dirtyDate, dirtyOptions);
  var year = date.getUTCFullYear();

  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale &&
    locale.options &&
    locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate =
    localeFirstWeekContainsDate == null
      ? 1
      : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate =
    options.firstWeekContainsDate == null
      ? defaultFirstWeekContainsDate
      : toInteger(options.firstWeekContainsDate);

  // Test if weekStartsOn is between 1 and 7 _and_ is not NaN
  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively')
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, dirtyOptions);

  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, dirtyOptions);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year
  } else {
    return year - 1
  }
}

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function startOfUTCWeekYear (dirtyDate, dirtyOptions) {
  if (arguments.length < 1) {
    throw new TypeError('1 argument required, but only ' + arguments.length + ' present')
  }

  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale &&
    locale.options &&
    locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate =
    localeFirstWeekContainsDate == null
      ? 1
      : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate =
    options.firstWeekContainsDate == null
      ? defaultFirstWeekContainsDate
      : toInteger(options.firstWeekContainsDate);

  var year = getUTCWeekYear(dirtyDate, dirtyOptions);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, dirtyOptions);
  return date
}

var MILLISECONDS_IN_WEEK$1 = 604800000;

// This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376
function getUTCWeek(dirtyDate, options) {
  if (arguments.length < 1) {
    throw new TypeError(
      '1 argument required, but only ' + arguments.length + ' present'
    )
  }

  var date = toDate(dirtyDate);
  var diff =
    startOfUTCWeek(date, options).getTime() -
    startOfUTCWeekYear(date, options).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1
}

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

var formatters$1 = {
  // Era
  G: function(date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, { width: 'abbreviated' })
      // A, B
      case 'GGGGG':
        return localize.era(era, { width: 'narrow' })
      // Anno Domini, Before Christ
      case 'GGGG':
      default:
        return localize.era(era, { width: 'wide' })
    }
  },

  // Year
  y: function(date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, { unit: 'year' })
    }

    return formatters.y(date, token)
  },

  // Local week-numbering year
  Y: function(date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2)
    }

    // Ordinal number
    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, { unit: 'year' })
    }

    // Padding
    return addLeadingZeros(weekYear, token.length)
  },

  // ISO week-numbering year
  R: function(date, token) {
    var isoWeekYear = getUTCISOWeekYear(date);

    // Padding
    return addLeadingZeros(isoWeekYear, token.length)
  },

  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function(date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length)
  },

  // Quarter
  Q: function(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter)
      // 01, 02, 03, 04
      case 'QQ':
        return addLeadingZeros(quarter, 2)
      // 1st, 2nd, 3rd, 4th
      case 'Qo':
        return localize.ordinalNumber(quarter, { unit: 'quarter' })
      // Q1, Q2, Q3, Q4
      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        })
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        })
      // 1st quarter, 2nd quarter, ...
      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        })
    }
  },

  // Stand-alone quarter
  q: function(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter)
      // 01, 02, 03, 04
      case 'qq':
        return addLeadingZeros(quarter, 2)
      // 1st, 2nd, 3rd, 4th
      case 'qo':
        return localize.ordinalNumber(quarter, { unit: 'quarter' })
      // Q1, Q2, Q3, Q4
      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        })
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        })
      // 1st quarter, 2nd quarter, ...
      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        })
    }
  },

  // Month
  M: function(date, token, localize) {
    var month = date.getUTCMonth();
    switch (token) {
      case 'M':
      case 'MM':
        return formatters.M(date, token)
      // 1st, 2nd, ..., 12th
      case 'Mo':
        return localize.ordinalNumber(month + 1, { unit: 'month' })
      // Jan, Feb, ..., Dec
      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        })
      // J, F, ..., D
      case 'MMMMM':
        return localize.month(month, { width: 'narrow', context: 'formatting' })
      // January, February, ..., December
      case 'MMMM':
      default:
        return localize.month(month, { width: 'wide', context: 'formatting' })
    }
  },

  // Stand-alone month
  L: function(date, token, localize) {
    var month = date.getUTCMonth();
    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1)
      // 01, 02, ..., 12
      case 'LL':
        return addLeadingZeros(month + 1, 2)
      // 1st, 2nd, ..., 12th
      case 'Lo':
        return localize.ordinalNumber(month + 1, { unit: 'month' })
      // Jan, Feb, ..., Dec
      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        })
      // J, F, ..., D
      case 'LLLLL':
        return localize.month(month, { width: 'narrow', context: 'standalone' })
      // January, February, ..., December
      case 'LLLL':
      default:
        return localize.month(month, { width: 'wide', context: 'standalone' })
    }
  },

  // Local week of year
  w: function(date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, { unit: 'week' })
    }

    return addLeadingZeros(week, token.length)
  },

  // ISO week of year
  I: function(date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, { unit: 'week' })
    }

    return addLeadingZeros(isoWeek, token.length)
  },

  // Day of the month
  d: function(date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), { unit: 'date' })
    }

    return formatters.d(date, token)
  },

  // Day of year
  D: function(date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, { unit: 'dayOfYear' })
    }

    return addLeadingZeros(dayOfYear, token.length)
  },

  // Day of week
  E: function(date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        })
      // T
      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        })
      // Tu
      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        })
      // Tuesday
      case 'EEEE':
      default:
        return localize.day(dayOfWeek, { width: 'wide', context: 'formatting' })
    }
  },

  // Local day of week
  e: function(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek)
      // Padded numerical value
      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2)
      // 1st, 2nd, ..., 7th
      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, { unit: 'day' })
      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        })
      // T
      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        })
      // Tu
      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        })
      // Tuesday
      case 'eeee':
      default:
        return localize.day(dayOfWeek, { width: 'wide', context: 'formatting' })
    }
  },

  // Stand-alone local day of week
  c: function(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek)
      // Padded numerical value
      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length)
      // 1st, 2nd, ..., 7th
      case 'co':
        return localize.ordinalNumber(localDayOfWeek, { unit: 'day' })
      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        })
      // T
      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        })
      // Tu
      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        })
      // Tuesday
      case 'cccc':
      default:
        return localize.day(dayOfWeek, { width: 'wide', context: 'standalone' })
    }
  },

  // ISO day of week
  i: function(date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek)
      // 02
      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length)
      // 2nd
      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, { unit: 'day' })
      // Tue
      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        })
      // T
      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        })
      // Tu
      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        })
      // Tuesday
      case 'iiii':
      default:
        return localize.day(dayOfWeek, { width: 'wide', context: 'formatting' })
    }
  },

  // AM or PM
  a: function(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        })
      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        })
      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        })
    }
  },

  // AM, PM, midnight, noon
  b: function(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        })
      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        })
      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        })
    }
  },

  // in the morning, in the afternoon, in the evening, at night
  B: function(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        })
      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        })
      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        })
    }
  },

  // Hour [1-12]
  h: function(date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, { unit: 'hour' })
    }

    return formatters.h(date, token)
  },

  // Hour [0-23]
  H: function(date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), { unit: 'hour' })
    }

    return formatters.H(date, token)
  },

  // Hour [0-11]
  K: function(date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, { unit: 'hour' })
    }

    return addLeadingZeros(hours, token.length)
  },

  // Hour [1-24]
  k: function(date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, { unit: 'hour' })
    }

    return addLeadingZeros(hours, token.length)
  },

  // Minute
  m: function(date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), { unit: 'minute' })
    }

    return formatters.m(date, token)
  },

  // Second
  s: function(date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), { unit: 'second' })
    }

    return formatters.s(date, token)
  },

  // Fraction of second
  S: function(date, token) {
    return formatters.S(date, token)
  },

  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z'
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset)

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case 'XXXX':
      case 'XX': // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset)

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ':')
    }
  },

  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset)

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case 'xxxx':
      case 'xx': // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset)

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ':')
    }
  },

  // Timezone (GMT)
  O: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':')
      // Long
      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':')
    }
  },

  // Timezone (specific non-location)
  z: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':')
      // Long
      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':')
    }
  },

  // Seconds timestamp
  t: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length)
  },

  // Milliseconds timestamp
  T: function(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length)
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours)
  }
  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2)
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2)
  }
  return formatTimezone(offset, dirtyDelimiter)
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes
}

function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({ width: 'short' })
    case 'PP':
      return formatLong.date({ width: 'medium' })
    case 'PPP':
      return formatLong.date({ width: 'long' })
    case 'PPPP':
    default:
      return formatLong.date({ width: 'full' })
  }
}

function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({ width: 'short' })
    case 'pp':
      return formatLong.time({ width: 'medium' })
    case 'ppp':
      return formatLong.time({ width: 'long' })
    case 'pppp':
    default:
      return formatLong.time({ width: 'full' })
  }
}

function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/);
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong)
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({ width: 'short' });
      break
    case 'PP':
      dateTimeFormat = formatLong.dateTime({ width: 'medium' });
      break
    case 'PPP':
      dateTimeFormat = formatLong.dateTime({ width: 'long' });
      break
    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({ width: 'full' });
      break
  }

  return dateTimeFormat
    .replace('{{date}}', dateLongFormatter(datePattern, formatLong))
    .replace('{{time}}', timeLongFormatter(timePattern, formatLong))
}

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];

function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1
}

function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1
}

function throwProtectedError(token) {
  if (token === 'YYYY') {
    throw new RangeError(
      'Use `yyyy` instead of `YYYY` for formating years; see: https://git.io/fxCyr'
    )
  } else if (token === 'YY') {
    throw new RangeError(
      'Use `yy` instead of `YY` for formating years; see: https://git.io/fxCyr'
    )
  } else if (token === 'D') {
    throw new RangeError(
      'Use `d` instead of `D` for formatting days of the month; see: https://git.io/fxCyr'
    )
  } else if (token === 'DD') {
    throw new RangeError(
      'Use `dd` instead of `DD` for formatting days of the month; see: https://git.io/fxCyr'
    )
  }
}

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

var escapedStringRegExp = /^'(.*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;

/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Su            |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Su            | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Su, Sa        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Su            |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Su            |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | AM, PM                          | a..aaa  | AM, PM                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bbb  | AM, PM, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 1, 2, ..., 11, 0                  |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 0001, ..., 999               |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 05/29/1453                        | 7     |
 * |                                 | PP      | May 29, 1453                      | 7     |
 * |                                 | PPP     | May 29th, 1453                    | 7     |
 * |                                 | PPPP    | Sunday, May 29th, 1453            | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 05/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | May 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | May 29th, 1453 at ...             | 7     |
 * |                                 | PPPPpppp| Sunday, May 29th, 1453 at ...     | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 9. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The second argument is now required for the sake of explicitness.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   format(new Date(2016, 0, 1))
 *
 *   // v2.0.0 onward
 *   format(new Date(2016, 0, 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
 *   ```
 *
 * - New format string API for `format` function
 *   which is based on [Unicode Technical Standard #35](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
 *   See [this post](https://blog.date-fns.org/post/unicode-tokens-in-date-fns-v2-sreatyki91jg) for more details.
 *
 * - Characters are now escaped using single quote symbols (`'`) instead of square brackets.
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formating years; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formating years; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formating days of the month; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formating days of the month; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * var result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * var result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * var result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */
function format(dirtyDate, dirtyFormatStr, dirtyOptions) {
  if (arguments.length < 2) {
    throw new TypeError(
      '2 arguments required, but only ' + arguments.length + ' present'
    )
  }

  var formatStr = String(dirtyFormatStr);
  var options = dirtyOptions || {};

  var locale$1 = options.locale || locale;

  var localeFirstWeekContainsDate =
    locale$1.options && locale$1.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate =
    localeFirstWeekContainsDate == null
      ? 1
      : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate =
    options.firstWeekContainsDate == null
      ? defaultFirstWeekContainsDate
      : toInteger(options.firstWeekContainsDate);

  // Test if weekStartsOn is between 1 and 7 _and_ is not NaN
  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError(
      'firstWeekContainsDate must be between 1 and 7 inclusively'
    )
  }

  var localeWeekStartsOn = locale$1.options && locale$1.options.weekStartsOn;
  var defaultWeekStartsOn =
    localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn =
    options.weekStartsOn == null
      ? defaultWeekStartsOn
      : toInteger(options.weekStartsOn);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively')
  }

  if (!locale$1.localize) {
    throw new RangeError('locale must contain localize property')
  }

  if (!locale$1.formatLong) {
    throw new RangeError('locale must contain formatLong property')
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value')
  }

  // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376
  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);

  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale$1,
    _originalDate: originalDate
  };

  var result = formatStr
    .match(longFormattingTokensRegExp)
    .map(function(substring) {
      var firstCharacter = substring[0];
      if (firstCharacter === 'p' || firstCharacter === 'P') {
        var longFormatter = longFormatters[firstCharacter];
        return longFormatter(substring, locale$1.formatLong, formatterOptions)
      }
      return substring
    })
    .join('')
    .match(formattingTokensRegExp)
    .map(function(substring) {
      // Replace two single quote characters with one single quote character
      if (substring === "''") {
        return "'"
      }

      var firstCharacter = substring[0];
      if (firstCharacter === "'") {
        return cleanEscapedString(substring)
      }

      var formatter = formatters$1[firstCharacter];
      if (formatter) {
        if (
          !options.useAdditionalWeekYearTokens &&
          isProtectedWeekYearToken(substring)
        ) {
          throwProtectedError(substring);
        }
        if (
          !options.useAdditionalDayOfYearTokens &&
          isProtectedDayOfYearToken(substring)
        ) {
          throwProtectedError(substring);
        }
        return formatter(utcDate, substring, locale$1.localize, formatterOptions)
      }

      if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
        throw new RangeError(
          'Format string contains an unescaped latin alphabet character `' +
            firstCharacter +
            '`'
        )
      }

      return substring
    })
    .join('');

  return result
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'")
}

var dateFormats$1 = {
	'short-date': 'MMM d/yy', // Feb 23/18 - when space is tight!
	'medium-date': 'MMM d, yyyy', // Feb 23, 2018
	'long-date': 'MMMM d, yyyy', // February 23, 2018

	'short-day-date': 'E..EEE, MMM d', // Tue, Jan 30
	'medium-day-date': 'EEEE, MMM d, yyyy', // Friday, Feb 23, 2018
	'long-day-date': 'EEEE, MMMM d, yyyy', // Friday, February 23, 2018

	'short-time': 'h a..aaa', // 10 AM
	'medium-time': 'h:mm a..aaa', // 10:22 AM
	'long-time': 'h:mm:ss a..aaa', // 10:22:35 AM

	// SPECIAL DATA FORMATS
	'date-input': 'yyyy-MM-dd', // For input.type="date"
	'time-input': 'HH:mm', // For input.type="time"
	'datetime-input': "yyyy-MM-dd'T'HH:mm", // For input.type="datetime-local"
	'isoTimezone': "yyyy-MM-dd'T'HH:mm:ssx", // ISO-8601 WITH timezone offset
	'iso': '' // Default format - means Date().toISOString (ISO-8601)


	/**
  * PUBLIC UTILITY to parse a date and returns it a formatted string.
  * Accepts anything that Moment can parse, like a string or js-date object.
  * Can pass a keyword for a standard date/time format, or a custom format.
  *
  * @public
  * @param {(string|Date)} dateVal
  * @param {string} [dateFmt]
  * @param {string} [timeFmt]
  * @returns {string}
  */
};function formatDate(dateVal, dateFmt, timeFmt) {
	if (!dateVal) return '';

	var date = parseDate$1(dateVal);

	// If the date is missing or invalid, log it and return 'Invalid Date'.
	// The return value will help developers instantly spot format errors.
	// noinspection JSCheckFunctionSignatures
	if (!isValid(date)) {
		console.log('The date provided to formatters.date is invalid: ' + dateVal);
		return 'Invalid Date';
	}

	var dt = dateFmt || '';
	var tm = timeFmt || '';

	// Accept an array like [ dateFmt, timeFmt ]
	if (dt && isArray(dt)) {
		dt = dateFmt[0];
		tm = dateFmt[1];
	}

	// See if this is a keyword like "iso" or "short-date"
	if (isString(dateFormats$1[dt])) {
		//
		dt = dateFormats$1[dt]; // NOTE: 'iso' -> ''
	}
	if (isString(dateFormats$1[tm])) {
		tm = dateFormats$1[tm];
	}

	return !dt && !tm ? date.toISOString() : !dt ? format(date, tm) : !tm ? format(date, dt) : format(date, dt + ' ' + tm);
}

/**
 * Helper to reformat test as all UPPER-CASE
 */

var formatLowerCase = function formatLowerCase(text) {
	var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { ignoreMixedCase: false };

	// handle incorrect data-type, like Null
	if (!text) return '';

	// IGNORE text that is Mixed Case?
	if (opts.ignoreMixedCase) {
		var mixedCase = text !== text.toUpperCase() && text !== text.toLowerCase();
		if (mixedCase) return text;
	}

	return text.toLowerCase();
};

/**
 * Helper to reformat text as Proper Case
 */

var formatProperCase = function formatProperCase(text, force) {
	// handle incorrect data-type, like Null
	if (!text) return '';

	// IGNORE text that is already mixed case
	if (!force) {
		var mixedCase = text !== text.toUpperCase() && text !== text.toLowerCase();
		if (mixedCase) return text;
	}

	return text.toLowerCase().replace(/^(.)|[\s/-](.)/g, function ($1) {
		return $1.toUpperCase();
	});
};

/**
 * Helper to reformat test as all UPPER-CASE
 */
var formatUpperCase = function formatUpperCase(text) {
	var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { ignoreMixedCase: false };

	// handle incorrect data-type, like Null
	if (!text) return '';

	// IGNORE text that is Mixed Case?
	if (opts.ignoreMixedCase) {
		var mixedCase = text !== text.toUpperCase() && text !== text.toLowerCase();
		if (mixedCase) return text;
	}

	return text.toUpperCase();
};

var formatNumbersOnly = function formatNumbersOnly(value) {
	return isNumber(value) ? value + '' // coerce to a string
	: isString(value) ? (value.match(/[0-9]/g) || []).join('') : value // Unknown data-type
	;
};

var reDissectParts = /([01]+)?([0-9]{0,3})([0-9]{3})([0-9]{4})(.+)?/;

var formatPhone = function formatPhone(value) {
	if (!value) return '';

	var numbers = formatNumbersOnly(value || '');
	var parts = numbers.match(reDissectParts);

	if (parts) {
		var display = parts[3] + '-' + parts[4];

		if (parts[2]) {
			display = parts[2] + '-' + display;
		}
		if (parts[1]) {
			display = parts[1] + '-' + display;
		}
		if (parts[5]) {
			display = display + ' ' + parts[5];
		}

		return display;
	}

	// If parsing failed, return the original value
	return value;
};

var formatters$2 = {
	date: formatDate,
	dateFormats: dateFormats$1,
	lowerCase: formatLowerCase,
	properCase: formatProperCase,
	upperCase: formatUpperCase,
	numbersOnly: formatNumbersOnly,
	phone: formatPhone
};

/**
 * RegExp pattern tester; can handle numeric values
 *
 * @param {(string|number|null)} value
 * @param {(RegExp|string)} regex
 * @returns {boolean}
 */
var pattern = function pattern(value, regex) {
	if (isNil(value)) return false;
	var re = isRegExp(regex) ? regex : new RegExp(regex.toString());
	return re.test(value.toString());
};

// eslint-disable-next-line
var reEmail = /^\s*(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))\s*$/;

/**
 * Email validator taken from ReactNative email validator
 * NOTE that this may not handle highly abnormal email addresses!
 *
 * @param {string} value
 * @returns {boolean}
 */
var email = function email(value) {
	return reEmail.test(value.toLowerCase());
};

/**
 * @param {string} value
 * @returns {boolean}
 */
var phone = function phone(value) {
	var nums = formatters$2.numbersOnly(value || '');
	return nums.length === 7 || nums.length === 10 || nums.length === 11;
};

var string = function string(value) {
	return isString(value);
};

var number = function number(value, opts) {
	return isNumeric(value, opts);
};

var integer = function integer(value) {
	var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	return isNumeric(value, _extends({}, opts, { integer: true }));
};

var minLength = function minLength(value, len) {
	return trim(value.toString()).length >= len;
};

var maxLength = function maxLength(value, len) {
	return trim(value.toString()).length <= len;
};

var lengthRange = function lengthRange(value, lens) {
	return inRange(trim(value.toString()).length, lens[0], lens[1]);
};

var exactLength = function exactLength(value, len) {
	return trim(value.toString()).length === len;
};

var minNumber = function minNumber(value, num) {
	return +value >= num;
};

var maxNumber = function maxNumber(value, num) {
	return +value < num;
};

var numberRange = function numberRange(value, nums) {
	return inRange(value, nums[0], nums[1]);
};

var RE_PASSWORD_SYMBOLS = /[ /$^.*+()[\]!"#%&',\-:;<=>?@_`{|}~]/g;
var RE_NOT_PASSWORD_SYMBOLS = /[^ /$^.*+()[\]!"#%&',\-:;<=>?@_`{|}~]/g;
var stringContentTest = function stringContentTest(str, count, re) {
	if (!count) return true;
	var arr = str.match(re);
	return arr && arr.length >= (count > 0 ? count : 1);
};
var password = function password(value, opts, fieldName, messages) {
	if (!opts || isEmpty(opts)) {
		return string(value);
	}

	var toNum = function toNum(val) {
		return val === true ? 1 : val === false ? 0 : val || 0;
	};
	var lower = toNum(opts.lower);
	var upper = toNum(opts.upper);
	var num = toNum(opts.number);
	var symbol = toNum(opts.symbol);

	var hasLower = stringContentTest(value, lower, /[a-z]/g);
	var hasUpper = stringContentTest(value, upper, /[A-Z]/g);
	var hasNumber = stringContentTest(value, num, /[0-9]/g);
	var hasSymbol = stringContentTest(value, symbol, RE_PASSWORD_SYMBOLS);

	var msg = function msg(type, val) {
		return (messages[type] || '').replace(/{value}/, val);
	};
	var errors = [];

	if (!hasLower || !hasUpper) {
		if (lower === 1 && upper === 1) {
			errors.push(msg('mixedCase'));
		} else {
			if (!hasLower) errors.push(msg('lowerCase', lower));
			if (!hasUpper) errors.push(msg('upperCase', upper));
		}
	}

	if (!hasNumber) errors.push(msg('number', num));

	if (!hasSymbol) errors.push(msg('symbol', symbol));

	// Check for INVALID symbols
	var symbols = value.match(/[^a-z0-9]/gi) || [];
	var invalidSymbols = symbols.join('').match(RE_NOT_PASSWORD_SYMBOLS) || [];
	if (invalidSymbols.length) {
		errors.push(msg('invalidChars', uniq(invalidSymbols).join(' ')));
	}

	return errors.length ? errors : true;
};

var boolean = function boolean(value) {
	return isBoolean(value) || value === 0 || value === 1 || isString(value) && /^(|0|1|true|false|yes|no)$/.test(value);
};

var date = function date(value) {
	return !isNaN(parseDate$1(value));
};

var minDate = function minDate(value, dt) {
	var val = parseDate$1(value);
	var cmp = parseDate$1(dt);
	return isSameDay(val, cmp) || isAfter(val, cmp);
};

var maxDate = function maxDate(value, dt) {
	var val = parseDate$1(value);
	var cmp = parseDate$1(dt);
	return isSameDay(val, cmp) || isBefore(val, cmp);
};

// NOTE: dateRange ERROR if 1st comparison date is AFTER 2nd comparison
var dateRange = function dateRange(value, dts) {
	var dt = parseDate$1(value);
	var range = {
		start: parseDate$1(dts[0]),
		end: parseDate$1(dts[1])
	};
	if (isValid(dt) && isValid(range.start) && isValid(range.end)) {
		return isWithinInterval(dt, range);
	}

	return false; // BAD DATA!
};

var minTime = function minTime(value, tm) {
	var val = toTime(value);
	var cmp = toTime(tm);
	return isEqual$1(val, cmp) || isAfter(val, cmp);
};

var maxTime = function maxTime(value, tm) {
	var val = toTime(value);
	var cmp = toTime(tm);
	return isEqual$1(val, cmp) || isBefore(val, cmp);
};

var timeRange = function timeRange(value, tms) {
	return dateRange(toTime(value), [toTime(tms[0]), toTime(tms[1])]);
};

function toTime(value) {
	var dt = parseDate$1(value);
	dt = setYear(dt, 1970);
	dt = setDayOfYear(dt, 1);
	return dt;
}

function isNumeric(value) {
	var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { integer: false, allowNegative: false };

	if (isNumber(value)) {
		return testNumber(value);
	}

	if (isString(value)) {
		var num = Number(value); // Note: NaN == false
		return num || num === 0 ? testNumber(num) : false;
	}

	// Only numbers and strings are acceptable values
	return false;

	function testNumber(num) {
		if (opts.integer && !isInteger(num)) return false;
		return opts.allowNegative ? true : num >= 0;
	}
}

/**
 * Keep for future ref
 *
 function callValidationFunction(fn, fieldConfig, fieldValue, thisInstance) {
	 let errors = fn(fieldValue, fieldConfig.name, thisInstance);

	 if (errors === false) {
	 }

	 // Errors must be a string or non-empty array
	 if (!errors || (!_.isArray(errors) && !_.isString(errors))) {
		 return [];
	 }
 }
 */

var defaultValidators = {
	email: email,
	phone: phone,
	password: password,
	pattern: pattern,

	boolean: boolean,
	string: string,
	number: number,
	integer: integer,

	minLength: minLength,
	maxLength: maxLength,
	lengthRange: lengthRange,
	exactLength: exactLength,

	minNumber: minNumber,
	maxNumber: maxNumber,
	numberRange: numberRange,

	date: date,
	minDate: minDate,
	maxDate: maxDate,
	dateRange: dateRange,

	minTime: minTime,
	maxTime: maxTime,
	timeRange: timeRange
};

var string$1 = function string(value) {
	return toString(value);
};

var integer$1 = function integer(value) {
	return isNil(value) ? null : toInteger$1(value);
};

var number$1 = function number(value) {
	return isNil(value) ? null : toNumber(value);
};

var boolean$1 = function boolean(value) {
	if (isBoolean(value)) return value;

	// DO NOT convert NULL or undefined to boolean - these mean 'unspecified'
	if (isNil(value)) return null;

	// Any non-zero number is true
	if (isNumber(value)) return value !== 0;

	// Any string other than these special string values becomes true
	if (isString(value)) {
		var val = trim(value);
		return val && !/^(|0|false|no)$/.test(value);
	}

	// For all other types, convert truthy/falsey to a boolean
	return !!value;
};

var date$1 = function date(value) {
	var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'YYYY-MM-DD';
	return formatDate(value, format);
};
var dateISO = function dateISO(value) {
	return formatDate(value, 'iso');
};
var dateISOTimezone = function dateISOTimezone(value) {
	return formatDate(value, 'isoTimezone');
};

var dateObject = function dateObject(value) {
	if (!value) return null;
	if (isDate(value)) return value;
	// Avoid arrays and objects that will result in a Now date
	if (!isString(value) && !isNumber(value)) return null;

	return parseDate$1(value);
};

var defaultConverters = {
	boolean: boolean$1,
	string: string$1,
	integer: integer$1,
	number: number$1,
	date: date$1,
	dateISO: dateISO,
	dateISOTimezone: dateISOTimezone,
	dateObject: dateObject
};

/**
 * Internal form configuration defaults. Overridden by config passed-in.
 */
var formConfig = {
	// Specify initial form state, data and errors
	initialState: {},
	initialData: {},
	initialErrors: {},

	// CUSTOMIZE HELPER METHODS AND ERROR-MESSAGES
	// Built-in defaults will be added by initFormConfiguration()
	formatters: {},
	validators: {},
	converters: {},
	errorMessages: {},

	// form.onChange & onBlur is fired after ANY field same-event is fired
	onChange: undefined,
	onBlur: undefined,

	// Map of aliasName(s) to real field.name(s)
	// AUTO-GENERATED - CONFIG DATA WILL BE IGNORED
	fieldAliasMap: {},

	fieldDefaults: {
		/*
   * Form validation options can be overridden per-field
   * Validation can be triggered manually for a single field, or all fields
   * Separate options exist for validating a field with or without errors
   */
		// When should we do an INITIAL auto-validation of each field
		validateOnChange: false,
		validateOnBlur: false,
		// When should we re-validation a field that currently has an error
		revalidateOnChange: false,
		revalidateOnBlur: false,

		// How should we return error messages (by default)
		returnErrorsAsString: true, // false = return error(s) in an array

		// Should fields be read-only or disabled by default?
		readOnly: false,
		disabled: false,

		// TEXT-FIELD CLEANING/FORMATTING OPTIONS
		cleaning: {
			cleanOnBlur: true, // Clean field-text onBlur
			trim: true, // Trim leading-/trailing--spaces
			trimInner: false, // Replace multi-spaces/tabs with single space
			reformat: '' // {(string|Function)} Apply a formatter to value
		}
	},

	/*
  * Form-fields configuration; SHOWN HERE AFTER NORMALIZATION TO A HASH.
  * Can be passed as a hash keyed by fieldName OR as an array.
  * When passed as an array, each item must include a 'name' key.
  * When passed as a hash, the key will be COPIED to item.name automatically.
  *
  * Only fields requiring validation or type-conversion need to be included,
  *  however it can be useful to include all fields as a reference
  */
	fields: {}
	/* SAMPLE field data using Hash format (same as internal format)
 fields: {
 	address: {
 		displayName:	'Address',  // REQUIRED IF want to use in errors
 		autoValidate:	'blur',	// OVERRIDE default form-level option
 		onChange:		this.onAddressChange // OPTIONAL change callback
 		validation: {
 			required:	true,
 			address:	true, // PRESET validations; TBD
 			minLength:  20,
 			maxLength:  120,
 		},
 		// All validations have a default error message, but...
 		// Form default messages can be overridden at field-level
 		// Use of template placeholders is optional
 		errorMessages: {
 			maxLength: '{name} cannot exceed {value} characters'
 		}
 	},
 	'person/age': {
 		aliasName:		'age', // name user passes to get data-props, etc.
 		displayName:	'Your Age',
 		defaultValue:	25,
 		dataType:		'integer',
 		validation: {
 			fieldType:		'dataISOString',
 			required:		true,
 			numberRange:	[ 18, 120 ],
 		},
 	},
 	expandSectionOne: {
 		name:	'expandSectionOne',
 		isData: false  // value stored in form-state, NOT form-data
 	}
 },
 */
};

/**
 * Default form-field options
 */
var defaultFieldConfig = {
	name: '',
	isData: true, // true = form.data[name]; false = state.form[name]
	dataType: '', // Convert value to this type for DATA OBJECT
	dataFormat: '', // Format to use for input/control DATA OBJECT
	valueType: '', // Convert value to this type for FIELD VALUE
	valueFormat: '', // Format to use for input/control FIELD VALUE
	inputType: '', // If set, dataProps() includes: `type={inputType}`
	onChange: undefined, // Callback after FM done processing event
	onBlur: undefined, // Callback after FM done processing event
	onFocus: undefined, // Callback after FM done processing event
	cleaning: {}, // Value-cleaning options
	validation: {}, // Validation options
	errorMessages: {} // Field overrides, and/or `custom` error message


	/*
  * Values in config.fieldDefaults will be inherited if not specified per-field
  */
};

var date$2 = formatters$2.date;

/**
 * Default error messages used for standard validation errors
 * Message strings can contain variables that will be substituted:
 * - {name} is replaced by 'displayName' from field-config, if it is set.
 *      Otherwise "This field" will be used to replace {name}.
 * - {value} is the value specified in the validation rule
 * - {value1} is the 1st array value when validation rules use an array
 * - {value2} is the 2nd array value, and so on
 */

var defaultErrorMessages = {
	required: '{name} is required',

	// Standard, preset validators - no values required
	phone: 'Enter a valid 10-digit phone number',
	address: 'Enter a valid address',
	email: 'This is not a valid email address.',
	number: '{name} must be a number',
	integer: '{name} must be only numbers',

	password: {
		default: 'This is not a valid password',
		mixedCase: '{name} must have both uppercase and lowercase characters,',
		lowerCase: '{name} must have {value} or more lowercase characters,',
		upperCase: '{name} must have {value} or more uppercase characters,',
		number: '{name} must have {value} or more numbers,',
		symbol: '{name} must have {value} or more symbols,',
		invalidChars: 'These characters are INVALID: {value}'
	},

	// Generic, preset validators - require value(s) to be set
	minLength: '{name} cannot be less than {value} characters',
	maxLength: '{name} cannot be more than {value} characters.',
	exactLength: '{name} must be exactly {value} characters.',

	minNumber: '{name} cannot be less than {value}',
	maxNumber: '{name} cannot be more than {value}',
	numberRange: '{name} must be between {value1} and {value2}',

	minDate: function minDate(name, value) {
		return (name || 'Date') + ' cannot be before ' + date$2(value, 'medium-date');
	},
	maxDate: function maxDate(name, value) {
		return (name || 'Date') + ' cannot be after ' + date$2(value, 'medium-date');
	},
	dateRange: function dateRange(name, value) {
		return (name || 'Date') + ' must be between ' + (date$2(value[0], 'medium-date') + ' and ' + date$2(value[1], 'medium-date'));
	},

	minTime: function minTime(name, value) {
		return (name || 'Time') + ' cannot be before ' + date$2(value, 'medium-time');
	},
	maxTime: function maxTime(name, value) {
		return (name || 'Time') + ' cannot be after ' + date$2(value, 'medium-time');
	},
	timeRange: function timeRange(name, value) {
		return (name || 'Time') + ' must be between ' + (date$2(value[0], 'medium-time') + ' and ' + date$2(value[1], 'medium-time'));
	},

	// The 'unknown' message should never be needed, but just in case...
	unknown: 'This entry is invalid'
};

/**
 * FormManager sub-component to handle form and field configuration
 *
 * @param {Object} formManager    FormManager instance object
 * @param {Object} [components]        Hash of FormManager sub-components
 * @returns {Object}                Config API for this instance
 * @constructor
 */
function Config(formManager, components) {
	// Auto-instantiate so 'new' keyword is NOT required
	// (It's best practice to not require 'new' as it's an internal design)
	if (!(this instanceof Config)) {
		return new Config(formManager, components);
	}

	var internal = components.internal;

	// Form-Configuration cache - data accessible only via config methods

	var config = {};

	/**
  * CONFIG API - provides simplified method names for internal functions
  */
	return {
		// Methods used internally by FormManager components
		init: init,
		get: getAtPath,
		getField: getFieldConfig, // GETTER for field configuration
		setField: setFieldConfig, // SETTER for field configuration
		getValidation: getFieldValidation, // GETTER for field configuration
		setValidation: setFieldValidation, // SETTER for field configuration
		aliasToRealName: aliasToRealName,
		withFieldDefaults: withFieldDefaults,

		// Methods exposed in FormManager API
		publicAPI: {
			setConfig: setConfig, // SETTER for ANY configuration keys
			setFieldDefaults: setFieldDefaults, // SETTER for fieldDefaults config
			getFieldConfig: getFieldConfig, // GETTER for field configuration
			setFieldConfig: setFieldConfig, // SETTER for field configuration
			getFieldValidation: getFieldValidation, // GETTER for validation config
			setFieldValidation: setFieldValidation, // SETTER for validation config
			isFieldRequired: isFieldRequired, // GETTER for validation.required
			setFieldRequired: setFieldRequired, // SETTER for validation.required
			isFieldDisabled: isFieldDisabled, // GETTER for field.disabled
			setFieldDisabled: setFieldDisabled, // SETTER for field.disabled
			isFieldReadOnly: isFieldReadOnly, // GETTER for field.readOnly
			setFieldReadOnly: setFieldReadOnly, // SETTER for field.readOnly
			setDefaultDisabled: setDefaultDisabled, // SETTER for fieldDefaults.disabled
			setDefaultReadOnly: setDefaultReadOnly, // SETTER for fieldDefaults.readOnly
			// Aliases
			isRequired: isFieldRequired, // GETTER for validation.required
			setRequired: setFieldRequired, // SETTER for validation.required
			isDisabled: isFieldDisabled, // GETTER for field.disabled
			setDisabled: setFieldDisabled, // SETTER for field.disabled
			isReadOnly: isFieldReadOnly, // GETTER for field.readOnly
			setReadOnly: setFieldReadOnly // SETTER for field.readOnly
		}

		/**
   * Initialization method to set internal configuration.
   * NOTE that config.fields can be an array OR a hash keyed by fieldname.
   */
	};function init(options) {
		// IGNORE option keys auto-generated by setFieldConfig()
		merge(config, omit(options, ['fields', 'fieldAliasMap']));

		// Add all default form values
		defaultsDeep(config, formConfig);
		defaults$1(config.formatters, formatters$2);
		defaults$1(config.validators, defaultValidators);
		defaults$1(config.converters, defaultConverters);
		defaults$1(config.errorMessages, defaultErrorMessages);

		// Now configure each form-field
		if (options.fields) {
			setFieldConfig(options.fields);
		}
	}

	/**
  * Set field-config for one or more fields
  * Accepts an array -or- hash keyed by fieldname to set multiple fields.
  *
  * @param {Object} options		Hash of configuration changes
  * @returns {Object}            All SETTERS return instance for chaining
  */
	function setConfig(options) {
		// IGNORE option keys auto-generated by setFieldConfig()
		merge(config, omit(options, ['fields', 'fieldAliasMap']));

		// Now configure each form-field
		if (options.fields) {
			setFieldConfig(options.fields);
		}

		internal.triggerComponentUpdate();

		return formManager;
	}

	function getAtPath(path) {
		return getObjectValue(config, path);
	}

	/**
  * Get field-config for ONE-OR-ALL FIELDS
  *
  * @param {string} [name]
  * @returns {Object}    Returns fieldConfig for one or all fields
  */
	function getFieldConfig(name) {
		var fieldName = aliasToRealName(name);
		var fields = config.fields;
		return cloneDeep(name ? fields[fieldName] : fields);
	}

	/**
  * Set field-config for one or more fields
  * Accepts an array -or- hash keyed by fieldname to set multiple fields.
  *
  * @param {(string|Array|Object)} [name]
  * @param {(Object|Array|string)} [data]
  * @param {Object} [opts]       Option for merging with existing config
  * @returns {Object}            All SETTERS return instance for chaining
  */
	function setFieldConfig(name, data) {
		var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

		var isNamePassed = isString(name);

		// 2nd & 3rd arguments SHIFT if 'name' (string) is not passed
		var configData = isNamePassed ? data : name;
		var configOpts = merge({ replace: false, fieldName: '' }, isNamePassed ? opts : data);
		var fields = config.fields;

		var set = function set(n, newConfigData, replace) {
			// A field-name is required
			if (!n) return;

			var fieldName = aliasToRealName(n);
			var fieldConfig = fields[fieldName];

			// INIT fieldConfig if not exists, OR if opts.replace specified
			if (!fieldConfig || replace) {
				fieldConfig = fields[fieldName] = cloneDeep(defaultFieldConfig);
			}

			merge(fieldConfig, newConfigData);

			// ensure field.name is set - may have been passed as opts.fieldName
			fieldConfig.name = fieldName;

			if (fieldConfig.aliasName) {
				config.fieldAliasMap[fieldConfig.aliasName] = fieldName;
			}
		};

		// SINGLE FIELD UPDATE
		if (isNamePassed) {
			set(name, configData, configOpts.replace);
		}
		// SINGLE -OR- MULTIPLE FIELDS, AS A HASH...
		else if (isPlainObject(configData)) {
				var singleFieldName = configData.name || configOpts.fieldName;

				// SINGLE FIELD UPDATE
				if (singleFieldName && isString(singleFieldName)) {
					set(singleFieldName, configData, configOpts.replace);
				}
				// MULTIPLE FIELD UPDATE (keyed by fieldname)
				else {
						forOwn(configData, function (cfg, n) {
							set(n, cfg, true);
						});
					}
			}
			// MULTIPLE FIELD UPDATE - CONFIGS AS ARRAY
			// (requires a 'name' key inside each config hash)
			else if (isArray(configData)) {
					var _iteratorNormalCompletion = true;
					var _didIteratorError = false;
					var _iteratorError = undefined;

					try {
						for (var _iterator = configData[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
							var cfg = _step.value;

							if (isPlainObject(cfg)) {
								set(cfg.name, cfg, true);
							}
						}
					} catch (err) {
						_didIteratorError = true;
						_iteratorError = err;
					} finally {
						try {
							if (!_iteratorNormalCompletion && _iterator.return) {
								_iterator.return();
							}
						} finally {
							if (_didIteratorError) {
								throw _iteratorError;
							}
						}
					}
				}

		internal.triggerComponentUpdate();

		return formManager;
	}

	/**
  * Set field-config for one or more fields
  * Accepts an array -or- hash keyed by fieldname to set multiple fields.
  *
  * @param {Object} [settings]
  * @returns {Object}            All SETTERS return instance for chaining
  */
	function setFieldDefaults(settings) {
		merge(config.fieldDefaults, settings);

		internal.triggerComponentUpdate();

		return formManager;
	}

	/**
  * PUBLIC GETTER for field validation options
  *
  * @public
  * @param {string} name     Fieldname to get
  * @returns {Object}        Validation-config in getter mode
  */
	function getFieldValidation(name) {
		return cloneDeep(getField(name, 'validation'));
	}

	/**
  * PUBLIC SETTER for field validation options
  *
  * @public
  * @param {string} name             Fieldname to get/set
  * @param {Object} [settings]       New Validation setting(s)
  * @param {Object} [opts]            Option for merging with existing config
  * @returns {Object}                All SETTERS return instance for chaining
  */
	function setFieldValidation(name, settings) {
		var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : { merge: false };

		var newSettings = cloneDeep(settings);
		return setField(name, 'validation', newSettings, opts);
	}

	/**
  * PUBLIC GETTER for 'validation.required' flag.
  *
  * @public
  * @param {(string|Array)} name    Fieldname (or array of), for mass update
  * @returns {(boolean|Object)}     Returns true/false OR a hash of values
  */
	function isFieldRequired(name) {
		return getField(name, 'validation.required') || false;
	}

	/**
  * PUBLIC SETTER for validation 'required' flag.
  *
  * @public
  * @param {(Array|string)} name     Fieldname (or array of), for mass update
  * @param {boolean} required        Set this field(s) required?
  * @returns {Object}                All SETTERS return instance for chaining
  */
	function setFieldRequired(name) {
		var required = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

		return setField(name, 'validation.required', !!required);
	}

	/**
  * PUBLIC GETTER for field.disabled flag.
  *
  * @public
  * @param {(string|Array)} name    Fieldname (or array of), for mass update
  * @returns {(boolean|Object)}     Returns true/false OR a hash of values
  */
	function isFieldDisabled(name) {
		var disabled = getField(name, 'disabled');
		return disabled || config.fieldDefaults.disabled || false;
	}

	/**
  * PUBLIC SETTER for field.disabled flag.
  * Setting 'false' means REMOVE the disabled flag so will use fieldDefaults
  *
  * @public
  * @param {(Array|string)} name    Fieldname (or array of), for mass update
  * @param {boolean} disabled       Set this field disabled?
  * @returns {Object}               All SETTERS return instance for chaining
  */
	function setFieldDisabled(name) {
		var disabled = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

		return setField(name, 'disabled', !!disabled || undefined);
	}

	/**
  * PUBLIC SETTER for fieldDefaults.disabled flag.
  *
  * @public
  * @param {boolean} disabled    	Set all fields disabled?
  * @returns {Object}          		All SETTERS return instance for chaining
  */
	function setDefaultDisabled() {
		var disabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

		return setFieldDefaults({ disabled: !!disabled });
	}

	/**
  * PUBLIC GETTER for field.readOnly flag.
  *
  * @public
  * @param {(Array|string)} name    Fieldname (or array of), for mass update
  * @returns {(boolean|Object)}     Returns true/false OR a hash of values
  */
	function isFieldReadOnly(name) {
		var readOnly = getField(name, 'readOnly');
		return readOnly || config.fieldDefaults.readOnly || false;
	}

	/**
  * PUBLIC SETTER for field.readOnly flag.
  * Setting 'false' means REMOVE the readOnly flag so will use fieldDefaults
  *
  * @public
  * @param {(Array|string)} name    	Fieldname (or array of), for mass update
  * @param {boolean} readOnly    	Set this field disabled?
  * @returns {Object}            	All SETTERS return instance for chaining
  */
	function setFieldReadOnly(name) {
		var readOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

		return setField(name, 'readOnly', !!readOnly || undefined);
	}

	/**
  * PUBLIC SETTER for fieldDefaults.readOnly flag.
  *
  * @public
  * @param {boolean} readOnly    	Set all fields disabled?
  * @returns {Object}            	All SETTERS return instance for chaining
  */
	function setDefaultReadOnly() {
		var readOnly = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

		return setFieldDefaults({ readOnly: !!readOnly });
	}

	/**
  * Helper for getting field config values
  *
  * @param {string} name        	Fieldname - may be an alias
  * @param {string} branch    	Path inside field-config to return
  * @returns {(Object|*)} 		ONE Field-config-value for a field(s)
  */
	function getField(name, branch) {
		var get = function get(n) {
			var fieldName = aliasToRealName(n);
			var path = 'fields.' + fieldName + '.' + branch;
			return getObjectValue(config, path);
		};

		return isArray(name)
		// Return an object with passed names as the keys
		? name.reduce(function (obj, n) {
			obj[n] = get(n);return obj;
		}, {}) : get(name);
	}

	/**
  * Helper for setting field config values
  *
  * @param {string} name         Fieldname - may be an alias
  * @param {string} branch       Path inside field-config to return
  * @param {*} value            	Value to be set
  * @param {Object} [opts]       Options to pass to setObjectValue()
  * @returns {Object}       		All SETTERS return instance for chaining
  */
	function setField(name, branch, value) {
		var opts = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

		var somethingChanged = false;

		var set = function set(n) {
			var fieldName = aliasToRealName(n);
			// Must use array because fieldName may contain dots (nested field)
			var path = ['fields', fieldName, branch];
			var oldValue = getObjectValue(config, path);

			if (value !== oldValue) {
				setObjectValue(config, path, value, opts);
				somethingChanged = true;
			}
		};

		if (isArray(name)) {
			var _iteratorNormalCompletion2 = true;
			var _didIteratorError2 = false;
			var _iteratorError2 = undefined;

			try {
				for (var _iterator2 = name[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
					var n = _step2.value;
					set(n);
				}
			} catch (err) {
				_didIteratorError2 = true;
				_iteratorError2 = err;
			} finally {
				try {
					if (!_iteratorNormalCompletion2 && _iterator2.return) {
						_iterator2.return();
					}
				} finally {
					if (_didIteratorError2) {
						throw _iteratorError2;
					}
				}
			}
		} else {
			set(name);
		}

		if (somethingChanged) {
			internal.triggerComponentUpdate();
		}

		return formManager;
	}

	/**
  * Helper to convert aliasName to fieldName
  * @param name            The name OR aliasName of a field
  * @returns {string}    If alias found then mapped fieldName, else name
  */
	function aliasToRealName(name) {
		if (!name) return name;
		if (isArray(name)) return name.map(aliasToRealName);

		if (config.fields[name]) return name;

		var realName = config.fieldAliasMap[name];
		if (config.fields[realName]) return realName;

		// field nay not have a field-configuration, so can't find the name
		return name;
	}

	/**
  * Helper that looks for EITHER A field-specific OR default config option
  * @param {Object} fieldConfig
  * @param {string} path
  * @returns {undefined|*}
  */
	function withFieldDefaults(fieldConfig, path) {
		// If field specifies a value, use it!
		var fieldConfigValue = getObjectValue(fieldConfig, path);
		if (!isNil(fieldConfigValue)) return fieldConfigValue;

		var defaultValue = getObjectValue(config.fieldDefaults, path);
		if (!isNil(defaultValue)) return defaultValue;

		return undefined;
	}
}

/**
 * FormManager sub-component to handle form-field data
 *
 * @param {Object} formManager    FormManager instance object
 * @param {Object} components        Hash of FormManager sub-components
 * @returns {Object}                Config API for this instance
 * @constructor
 */
function Validation(formManager, components) {
	// Auto-instantiate so 'new' keyword is NOT required
	// (It's best practice to not require 'new' as it's an internal design)
	if (!(this instanceof Validation)) {
		return new Validation(formManager, components);
	}

	var config = components.config,
	    data = components.data,
	    internal = components.internal;

	// Extract helper methods from config for brevity

	var aliasToRealName = config.aliasToRealName,
	    withFieldDefaults = config.withFieldDefaults;

	// Form-Errors cache - data accessible only via config methods

	var stateOfErrors = {};

	/**
  * VALIDATION API - provides simplified method names for internal functions
  */
	return {
		// Methods used internally by FormManager components
		init: init,
		hasErrors: hasErrors, // GETTER - does field OR form have errors
		getErrors: getErrors, // GETTER for stateOfErrors
		setErrors: setErrors, // SETTER for stateOfErrors
		clearErrors: clearErrors, // SETTER - clears ALL errors for 1 field
		validate: validate, // ACTION for field OR form validation

		// Methods exposed in FormManager API
		publicAPI: {
			validate: validate, // ACTION for field OR form validation
			validateAll: validateAll, // ACTION for field OR form validation
			getErrors: getErrors, // GETTER for stateOfErrors
			setErrors: setErrors, // SETTER for stateOfErrors
			clearErrors: clearErrors, // SETTER - clears ALL errors for 1 field
			hasErrors: hasErrors, // GETTER - does field OR form have errors
			// ALIASES
			hasError: hasErrors,
			clearError: clearErrors,
			setError: setErrors,
			getError: getErrors,
			errors: getErrors
		}

		/**
   * Initialize form errors
   */
	};function init() {
		stateOfErrors = {}; // In case called for a RESET
	}

	function getFormErrors() {
		var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : { asArray: true };

		// Iterate everything in state.form.error to create list of errors
		// Error data is in a hash so can include fieldNames
		var arrErrors = [];

		forOwn(stateOfErrors, function (errors, fieldName) {
			if (errors && !isEmpty(errors)) {
				var error = {
					name: fieldName,
					errors: itemToArray(errors)

					// Add aliasName & displayName to error from field-config
				};
				var _ref = config.getField(fieldName) || {},
				    aliasName = _ref.aliasName,
				    displayName = _ref.displayName;

				if (aliasName) error.aliasName = aliasName;
				if (displayName) error.displayName = displayName;

				arrErrors.push(error);
			}
		});

		// NOTE: Is an empty array if there are no field-error
		// Auto-join errors into a string if option passed
		return opts.asArray === false ? arrErrors.join('\n') : arrErrors;
	}

	/**
  * Helper for error methods to keep things DRY
  */
	function getErrorsHash(fieldName) {
		// NOTE that we do NOT support nested structure for errors, so...
		// A 'who/gender' field stores errors at: form.error['who/gender']
		// However the field data is NESTED at:	form.data.who.gender
		var fieldErrors = stateOfErrors[fieldName] || {};

		// If field-errors is a string, normalize it to a custom error
		if (isString(fieldErrors)) {
			fieldErrors = {
				custom: fieldErrors
			};
		}

		return fieldErrors;
	}

	/**
  * PUBLIC GETTER to fetch error(s) for a specific field.
  * Errors are stored in a hash, which this converts to a simple array.
  * Calling code can output message(s) using join() or iterate array.
  *
  * @public
  * @param {string} name        Field-name or alias-name
  * @param {Object} opts        Options for return value
  * @returns {(string|Array)}
  */
	function getErrors(name) {
		var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { asArray: false };

		if (!name) {
			return getFormErrors();
		}

		// field MAY have an aliasName
		var fieldName = aliasToRealName(name);
		var fieldConfig = config.getField(fieldName);
		var errors = getErrorsHash(fieldName);

		// Iterate through all error-keys and combine errors into an array
		var arrErrors = itemToArray(errors);

		var returnAsString = withFieldDefaults(fieldConfig, 'returnErrorsAsString');
		// If a specific option was passed, it overrides field-configuration
		if (isBoolean(opts.asArray)) {
			returnAsString = !opts.asArray;
		}

		// Auto-join errors into a string if related config option is set,
		return returnAsString ? arrErrors.join('\n') : arrErrors;
	}

	/**
  * PUBLIC SETTER for 1 type of error on 1 field; can be an array of errors
  * Note: Errors can only be set ONE FIELD AT A TIME
  * Call this method multiple times to set errors on additional fields.
  *
  * @public
  * @param {string} name                Field-name or alias-name
  * @param {string} type                Type, eg: 'required', 'custom'
  * @param {(Array|string)} errorMsg    Error-message text for this type
  * @param {Object} opts                Merge options
  */
	function setErrors(name, type, errorMsg) {
		var opts = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

		var setOpts = Object.assign({ merge: true }, opts);
		var fieldName = aliasToRealName(name);
		var fieldErrors = setOpts.merge ? stateOfErrors[fieldName] || {} : {};

		// Field error values should be an array of messages, even if only one
		fieldErrors[type] = isString(errorMsg) ? [errorMsg] : errorMsg;

		// Multiple error-types as hash keyed by type
		if (isPlainObject(errorMsg)) {
			Object.assign(fieldErrors, cloneDeep(errorMsg));
		} else if (isArray(errorMsg)) {
			fieldErrors[type] = clone(errorMsg);
		} else {
			fieldErrors[type] = [errorMsg];
		}

		return formManager;
	}

	/**
  * PUBLIC SETTER for field errors - removes errors only
  * Can remove for 1 field (string), multiple fields (Array) or all (blank)
  *
  * @public
  * @param {(Array|string)} [name]        Field-name(s) and/or alias-name(s)
  */
	function clearErrors(name) {
		if (!name) {
			stateOfErrors = {};
		} else if (isArray(name)) {
			var _iteratorNormalCompletion = true;
			var _didIteratorError = false;
			var _iteratorError = undefined;

			try {
				for (var _iterator = name[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
					var n = _step.value;

					var fieldName = aliasToRealName(n);
					delete stateOfErrors[fieldName];
				}
			} catch (err) {
				_didIteratorError = true;
				_iteratorError = err;
			} finally {
				try {
					if (!_iteratorNormalCompletion && _iterator.return) {
						_iterator.return();
					}
				} finally {
					if (_didIteratorError) {
						throw _iteratorError;
					}
				}
			}
		} else {
			var _fieldName = aliasToRealName(name);
			delete stateOfErrors[_fieldName];
		}

		return internal.triggerComponentUpdate();
	}

	/**
  * PUBLIC GETTER to check if a specific field has any errors
  *
  * @public
  * @param {string} name        Field-name or alias-name
  * @returns {boolean}
  */
	function hasErrors(name) {
		if (name) {
			var fieldName = aliasToRealName(name);
			var fieldErrors = stateOfErrors[fieldName];
			return !!fieldErrors && !isEmpty(fieldErrors);
		} else {
			return !isEmpty(stateOfErrors);
		}
	}

	/**
  * @param {string} name         Field-name or alias-name
  * @param {*} val             	Field value to validate
  * @param {string} [event]      Event that triggered this, eg: 'change'
  * @param {boolean} [update]    Event that triggered this, eg: 'change'
  * @returns {Promise<boolean>}  Validation can be async so returns a promise
  */
	function validate(name, val, event) {
		var update = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;

		if (!name || isPlainObject(name)) {
			return validateAll(name);
		}

		// This CAN also be called directly to validate the 'current value'
		var value = !isUndefined(val) ? val : formManager.value(name);

		// field MAY have an aliasName
		var fieldName = aliasToRealName(name);
		var fieldConfig = config.getField(fieldName) || {};

		var forceValidation = event === 'validate';
		var suffixes = {
			blur: 'OnBlur',
			change: 'OnChange'

			// If an event was passed, check to see if we should validate or not.
			// If not, then abort without doing validation
		};if (event && !forceValidation) {
			var fieldHasErrors = hasErrors(fieldName);
			var prefix = fieldHasErrors ? 'revalidate' : 'validate';
			var suffix = suffixes[event];
			var isValidationEvent = withFieldDefaults(fieldConfig, '' + prefix + suffix);

			if (!isValidationEvent) {
				// ABORT by returning a resolved promise
				return Promise.resolve(!fieldHasErrors);
			}
		}

		// Return the validation promise - will return true if is-valid value
		return validateField(fieldName, value, { update: update });
	}

	/**
  * @param {Object} [opts]
  * @returns {Promise<boolean>}
  */
	function validateAll() {
		var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

		var fields = config.getField();
		var noUpdate = { update: false };
		var only = void 0,
		    skip = void 0;

		if (opts && isPlainObject(opts)) {
			only = arrayToHash(opts.only, true);
			skip = arrayToHash(opts.ignore, true);
		} else if (opts && isArray(opts)) {
			only = arrayToHash(opts, true);
		}

		var fieldValidations = [];
		var previousFieldErrors = cloneDeep(stateOfErrors);

		forOwn(fields, function (field, fieldName) {
			// Skip fields if specified in opts
			if (only && !only[fieldName]) return;
			if (skip && skip[fieldName]) return;
			// Skip fields that are really form-state; those don't validate
			if (field.isData === false) return;

			fieldValidations.push(validateField(fieldName, data.getValue(fieldName), noUpdate));
		});

		// WAIT for all validations to complete, then resolve
		return Promise.all(fieldValidations).then(function () {
			// If error state has changed IN ANY WAY, update component
			if (!isEqual(stateOfErrors, previousFieldErrors)) {
				internal.triggerComponentUpdate();
			}

			// Return Promise with true if NO errors; false otherwise
			var allFieldsErrorFree = isEmpty(stateOfErrors);
			return Promise.resolve(allFieldsErrorFree);
		});
	}

	/**
  * @param {string} fieldName
  * @param {*} value
  * @param {Object} opts
  * @returns {Promise}            Returns validation promise
  */
	function validateField(fieldName, value) {
		var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : { update: true };

		return new Promise(function (resolve) {
			var val = value;

			// Shallow clone current field-errors hash to use as local object.
			// Lodash clone() returns an empty object if a non-object is passed.
			var fieldErrors = clone(stateOfErrors[fieldName]) || {};
			var errorsChanged = false;

			// Ensure string-values are trimmed before processing
			if (isString(val)) val = trim(val);

			var validators = config.get('validators');

			var fieldConfig = config.getField(fieldName) || {};
			var validation = fieldConfig.validation,
			    errorMessages = fieldConfig.errorMessages;


			var messages = cloneDeep(errorMessages);
			defaultsDeep(messages, config.get('errorMessages'));

			// if field validation is not configured, then nothing to do!
			if (!validation || isEmpty(validation)) {
				return resolve(errorsChanged);
			}

			// If a validation function is specified, normalize to 'custom' type
			if (isFunction(validation)) {
				validation = { custom: validation };
			}

			// Check basic validators first and short-circuit if fail
			// Note that -0- (zero) IS VALID - use minNumber if need > 0
			// noinspection JSValidateTypes
			var isBlankField = val === '' || isNil(val);

			// If field is blank, set errorsChanged to notify that state has
			// changed
			if (isBlankField) {
				errorsChanged = !isEmpty(fieldErrors);
				// Clear any field errors on the field
				fieldErrors = {};
			}

			var testPromises = []; // Used like Promise.all(validationPromises)
			var addTest = function addTest(type, resp, ruleValue) {
				testPromises.push(promisify(resp).then(function () {
					return handleValidatorResp(type, resp, ruleValue);
				}));
			};

			// Validate required fields - clear error if no longer blank
			var required = validation.required;
			if (required) {
				var isError = false;

				// Call 'custom' validation if specified
				// This test should always be synchronous so no promises needed.
				if (isFunction(required)) {
					var resp = !required(val, fieldName, messages['required'] || '', formManager);
					handleValidatorResp('required', resp);
					// Check to see if an error was just set
					isError = !!stateOfErrors['required'];
				} else {
					isError = isBlankField;
				}

				// Add or remove the custom error based on response.
				setError('required', !isError, isError);

				// If failed required test then abort all further validation
				if (isError) {
					return finalizeValidation();
				}
			}

			// ABORT validation if field is blank/empty...
			if (isBlankField) {
				// ...EXCEPT for a custom validator, if one is set
				runValidatorFunction('custom');

				// Regardless of custom validator result, we are DONE
				return finalizeValidation();
			}

			// Validate value against field.validation rules specified
			forOwn(validation, function (opt, type) {
				// Check for a validation function
				if (isFunction(opt)) {
					runValidatorFunction(type);
				}
				// Else look for a standard validator with this type-name
				else if (validators[type]) {
						addTest(type, validators[type](val, opt, // Pass validation option; may just be 'true'
						fieldName, messages[type] || '', formManager), opt);
					}
			});

			// DONE - process the errors - return the setState promise
			return finalizeValidation();

			// === SUBROUTINES ===

			function runValidatorFunction(type) {
				var fn = validation[type];
				var typeMessages = messages[type] || '';
				if (isFunction(fn)) {
					addTest(type, fn(val, fieldName, typeMessages, formManager));
				}
			}

			function handleValidatorResp(type, resp, ruleValue) {
				// Add or remove error-type based on response
				if (isBoolean(resp)) {
					setError(type, resp, ruleValue);
				} else if (isString(resp) || isArray(resp)) {
					// Consider empty-string OR empty-array as no-error
					if (!resp.length) {
						setError(type, true);
					} else {
						setError(type, false, ruleValue, resp);
					}
				} else {
					// Consider any other type of response as inValid
					// Either configured or default error-message applies
					setError(type, false, ruleValue);
				}
			}

			/**
    * Subroutine to set or clear errors in stateOfErrors
    *
    * @param {string} type       Validation type, eg: 'minNumber'
    * @param {boolean} [isValid] If true then REMOVE this error, if set
    * @param {*} [ruleValue]     The rule settings for this validator
    * @param {(string|Array)} [errorMessage]  Custom validator message
    */
			function setError(type, isValid) {
				var ruleValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
				var errorMessage = arguments[3];

				if (!isValid) {
					// ADD errorMessage(s) to
					// stateOfErrors.[fieldName].[errorKey] Will use CUSTOM
					// errorMessage (template) if one was passed
					var typeError = '';

					// MAY be an array of errors passed; handle that
					if (isArray(errorMessage)) {
						// noinspection JSValidateTypes
						typeError = errorMessage.reduce(function (arr, msg) {
							// Ignore blank items in errorMessage array
							if (msg) {
								arr.push(createErrorFromTemplate(type, ruleValue, msg));
							}
							return arr;
						}, []);
					} else {
						typeError = createErrorFromTemplate(type, ruleValue, errorMessage);
					}

					// If error is 'required', remove all other errors first
					if (type === 'required') {
						fieldErrors = {};
					}

					if (fieldErrors[type] !== typeError) {
						fieldErrors[type] = typeError;
						errorsChanged = true;
					}
				} else if (fieldErrors[type]) {
					delete fieldErrors[type];
					errorsChanged = true;
				}
			}

			/**
    * Helper method to process Error Message templates
    *
    * @param {string} type
    * @param {*} [ruleValue]
    * @param {string} [customTemplate]
    * @returns {string}
    */
			function createErrorFromTemplate(type, ruleValue, customTemplate) {
				var displayName = fieldConfig.displayName;

				var nil = function nil(v) {
					return isNil(v) ? '' : v;
				};

				var template = customTemplate || messages[type] || messages.unknown;

				// Messages can be a hash of multiple messages, eg: 'password'
				if (isPlainObject(template)) {
					template = template.default || messages.unknown;
				}

				// A template can be a function for more advanced formatting
				if (isFunction(template)) {
					return template(displayName, ruleValue);
				}

				var message = template.replace(/^{name}/, displayName || 'This field').replace(/{name}/g, displayName || 'this field').replace(/{value}/g, nil(ruleValue));

				if (isArray(ruleValue)) {
					message = message.replace(/{value1}/g, nil(ruleValue[0])).replace(/{value2}/g, nil(ruleValue[1]));
				}

				return message;
			}

			/**
    * Subroutine to set the errors in stateOfErrors
    */
			function finalizeValidation() {
				// WAIT for validation tests to complete, then process and exit
				Promise.all(testPromises).then(function () {
					var isFieldErrorFree = isEmpty(fieldErrors);

					// if field-errors is empty, remove the entire field-errors
					// object
					if (isFieldErrorFree) {
						// If there is existing errors hash, then this is a
						// change
						errorsChanged = !!stateOfErrors[fieldName];
						delete stateOfErrors[fieldName];
					} else if (errorsChanged) {
						// Add the cloned fieldErrors - replace old value/object
						stateOfErrors[fieldName] = fieldErrors;
					}

					// Trigger component reload if something changed
					if (errorsChanged && opts.update !== false) {
						internal.triggerComponentUpdate();
					}

					// Resolve promise so calling code knows we are done.
					resolve(isFieldErrorFree);
				});
			}
		});
	}

	function promisify(resp) {
		var isValid = true; // for clarity

		if (resp && isFunction(resp.then)) {
			// Ensure any Promise error is caught so will subsequently resolve
			return resp.catch(function () {
				return isValid;
			});
		}

		return Promise.resolve(resp);
	}

	/**
  * Convert array to hash with 'true' values for easier value check
  */
	function arrayToHash(arr) {
		var isFieldNames = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

		if (!isArray(arr)) return arr;

		var hash = {};
		var _iteratorNormalCompletion2 = true;
		var _didIteratorError2 = false;
		var _iteratorError2 = undefined;

		try {
			for (var _iterator2 = arr[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
				var value = _step2.value;

				// Handle alias fieldNames in array
				if (isFieldNames) value = aliasToRealName(value);
				hash[value] = true;
			}
		} catch (err) {
			_didIteratorError2 = true;
			_iteratorError2 = err;
		} finally {
			try {
				if (!_iteratorNormalCompletion2 && _iterator2.return) {
					_iterator2.return();
				}
			} finally {
				if (_didIteratorError2) {
					throw _iteratorError2;
				}
			}
		}

		return hash;
	}
}

/**
 * FormManager - helper for managing form data, error, validation, etc.
 *
 * @param {(Object|Function)} componentObject    Class OR Hook setState method
 * @param {Object} [options]        Initial configuration of form
 * @param {Object} [extraData]            Data passed in at instantiation
 * @returns {Object}                    Returns API for this instance
 * @constructor
 */
function FormManager(componentObject) {
	var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	var extraData = arguments[2];

	// Auto-instantiate so 'new' keyword is NOT required
	// (It's best practice to not require 'new' as it's an internal design)
	if (!(this instanceof FormManager)) {
		return new FormManager(componentObject, options, extraData);
	}

	/**
  * PUBLIC API for this instance
  * This API is extended by each sub-component's public methods. See below.
  *
  * @public
  */
	var publicAPI = {
		getRevision: getRevision, // GETTER for formRevision number
		reset: initForm, // ACTION: Reset form data, errors & state.
		render: triggerComponentUpdate,
		update: triggerComponentUpdate,

		// FORM-ELEMENT PROPERTY SETTERS (HELPERS)
		dataProps: getDataProps, // HELPER for writing component props
		errorProps: getErrorProps, // HELPER for writing component props
		allProps: getAllProps, // HELPER for writing component props

		// FORM-ELEMENT EVENT-HANDLERS
		onFieldChange: onFieldChange, // EVENT HANDLER for field.onChange
		onFieldFocus: onFieldFocus, // EVENT HANDLER for field.onFocus
		onFieldBlur: onFieldBlur // EVENT HANDLER for field.onBlur
	};

	var formRevision = 0;

	var formInitialized = false;

	// Internal API so sub-components can access internal helper methods
	var internal = { fireEventCallback: fireEventCallback, triggerComponentUpdate: triggerComponentUpdate

		// Config sub-component, internal API
	};var config = Config(publicAPI, { internal: internal });
	config.init(options);
	Object.assign(publicAPI, config.publicAPI);

	// State sub-component, internal API
	var state = State(publicAPI, { internal: internal });
	state.init(options.initialState);
	Object.assign(publicAPI, state.publicAPI);

	// Data sub-component, internal API
	var data = Data(publicAPI, { config: config, state: state, internal: internal });
	data.init(options.initialData, extraData);
	Object.assign(publicAPI, data.publicAPI);

	// Validation sub-component, internal API
	var validation = Validation(publicAPI, { config: config, data: data, internal: internal });
	Object.assign(publicAPI, validation.publicAPI);

	// Extract helper methods from config API for brevity
	var aliasToRealName = config.aliasToRealName,
	    withFieldDefaults = config.withFieldDefaults;


	formInitialized = true;

	// RETURN THE FORM-MANAGER API/OBJECT
	return publicAPI;

	/**
  * PUBLIC METHOD to reset form data, errors & state.
  * Does not reinitialize or reset field configuration.
  * Does not remove any handlers or data bound after creation.
  *
  * @public
  */
	function initForm() {
		// RE-INIT all state-hashes
		config.init(options);
		state.init(options.initialState);
		data.init(options.initialData, extraData);
		validation.clearErrors();

		// Can be called as reset(), so return API
		return publicAPI;
	}

	function getRevision() {
		return formRevision;
	}

	/**
  * Method to trigger a setState action in React component
  */
	function triggerComponentUpdate() {
		// Trigger a re-render only if component has been initialized
		if (formInitialized) {
			// Increment unique index used as component-state value
			formRevision++;

			// noinspection JSUnresolvedVariable
			if (componentObject.setState) {
				// Class method
				componentObject.setState({ formRevision: formRevision });
			} else {
				// Hook setFormState method (or whatever its name is!)
				componentObject(formRevision);
			}
		}

		return publicAPI;
	}

	/**
  * PUBLIC HELPER for components to integrate with FormManager.
  *
  * @public
  * @param {string} name    Field-name or alias-name
  * @param {Object} [opts]    Options, like non-text field details
  * @returns {*}
  */
	function getDataProps(name) {
		var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { checkbox: false, radio: false };

		var fieldName = aliasToRealName(name);

		var defaults = config.get('fieldDefaults');
		var fieldConfig = Object.assign({}, defaults, config.getField(fieldName));
		var inputType = fieldConfig.inputType,
		    displayName = fieldConfig.displayName,
		    disabled = fieldConfig.disabled,
		    readOnly = fieldConfig.readOnly;


		var fieldValidations = config.getValidation(fieldName) || {};
		var required = fieldValidations.required;


		var value = data.getValue(fieldName);

		var props = {
			name: fieldName,
			value: value,
			'aria-label': displayName || fieldName, // can override in view
			onChange: onFieldChange,
			onFocus: onFieldFocus,
			onBlur: onFieldBlur
		};

		if (inputType) {
			var type = inputType;
			if (type === 'datetime') type = 'datetime-local';
			props.type = type;
		}

		if (opts.checkbox || inputType === 'checkbox') {
			// Checkboxes can only be true or false
			props.checked = config.get('converters').boolean(value);
			// Checkbox values MUST be a string - use default value if not
			if (!isString(value)) props.value = 'on';
		}

		if (required) props.required = true;
		if (disabled) props.disabled = true;
		if (readOnly) props.readOnly = true;

		return props;
	}

	/**
  * PUBLIC HELPER for components to integrate with FormManager.
  *
  * @public
  * @param {string} name    Field-name or alias-name
  * @returns {Object}        Props for components like a TextField
  */
	function getErrorProps(name) {
		return {
			error: validation.hasErrors(name),
			helperText: validation.getErrors(name)
			// FormHelperTextProps: {
			// 	className: 'hide-when-empty'
			// }
		};
	}

	/**
  * PUBLIC HELPER for components to integrate with FormManager.
  *
  * @public
  * @param {string} name    Field-name or alias-name
  * @param {Object} [opts]  Options, like non-text field details
  * @returns {*}
  */
	function getAllProps(name) {
		var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

		return _extends({}, getDataProps(name, opts), getErrorProps(name));
	}

	/**
  * Field.onChange event-handler
  * CURRENTLY the onFocus event is not used , but is included in API
  *    because may want to add onFocus options in the future.
  *
  * @param {(Object|string)} event_name	Event object OR fieldName
  * @param {*} [value]					Field value
  * @returns {Promise}					Validation promise
  */
	function onFieldChange(event_name, value) {
		var _parseEvent = parseEvent(event_name, value),
		    _parseEvent2 = slicedToArray(_parseEvent, 2),
		    name = _parseEvent2[0],
		    val = _parseEvent2[1];
		// Will fire onChange callbacks AFTER updating field-value


		return data.setValue(name, val, { event: 'change' });
	}

	/**
  * Field.onFocus event-handler
  * CURRENTLY the onFocus event is not used , but is included in API
  *	because may want to add onFocus options in the future.
  *
  * @param {(Object|string)} event_name	Event object OR fieldName
  * @param {*} [value]					Field value
  * @returns {Promise}					Dummy promise for consistency
  */
	function onFieldFocus(event_name, value) {
		var _parseEvent3 = parseEvent(event_name, value),
		    _parseEvent4 = slicedToArray(_parseEvent3, 2),
		    name = _parseEvent4[0],
		    val = _parseEvent4[1];

		var fieldConfig = config.getField(name);
		if (!fieldConfig) return; // INVALID name PASSED; ABORT

		fireEventCallback(fieldConfig.onFocus, val, fieldConfig.name);
		fireEventCallback(config.get('onFocus'), val, fieldConfig.name);

		return Promise.resolve(true); // Return value true means isValid
	}

	/**
  * Field.onBlur event-handler
  *
  * @param {(Object|string)} event_name	Event object OR fieldName
  * @param {*} [value]					Field value
  * @returns {Promise}					Validation promise
  */
	function onFieldBlur(event_name, value) {
		var _parseEvent5 = parseEvent(event_name, value),
		    _parseEvent6 = slicedToArray(_parseEvent5, 2),
		    name = _parseEvent6[0],
		    val = _parseEvent6[1];

		var fieldConfig = config.getField(name);
		if (!fieldConfig) return; // INVALID name PASSED; ABORT

		var fieldName = fieldConfig.name;
		// const hasErrors = validation.hasErrors(fieldName)

		var cleanOnBlur = withFieldDefaults(fieldConfig, 'cleaning.cleanOnBlur');
		if (cleanOnBlur) {
			var newValue = data.cleanValue(val, fieldConfig);
			if (newValue !== val) {
				// Pass 'change' event so will fire event with new value
				data.setValue(fieldName, newValue, { event: 'change' });
			}
		}

		return validation.validate(name, val, 'blur').then(function () {
			// Note: This will run even if validation did not run
			fireEventCallback(fieldConfig.onBlur, val, fieldName);
			fireEventCallback(config.get('onBlur'), val, fieldName);
		});
	}

	function fireEventCallback(func, value, fieldName) {
		if (isFunction(func)) {
			func(value, fieldName, publicAPI);
		}
	}

	/**
  * Helper for field event handlers.
  * Can be a normal event handler OR be called manually with (name, value).
  *
  * @param {(Object|string)} event	Event object OR fieldName
  * @param {*} [value]				Field value
  * @returns {[string, *]}			Array of [name, value]
  */
	function parseEvent(event, value) {
		var fieldName = '';
		var val = value;

		if (isString(event)) {
			// A fieldName was passed - method was called manually.
			fieldName = aliasToRealName(event);
			// If value was NOT also passed, then use current value.
			if (isUndefined(val)) {
				val = data.getValue(fieldName);
			}
		}
		// NORMAL fields return an event object
		// noinspection JSUnresolvedVariable
		if (isObject(event) && isObject(event.target)) {
			// noinspection JSUnresolvedVariable
			var field = event.target;
			fieldName = aliasToRealName(field.name || '');
			val = /checkbox/.test(field.type || '') ? field.checked : field.value;
		}

		return [fieldName, val];
	}
}

function useFormManager(formConfig, data) {
	// noinspection JSUnusedLocalSymbols
	var _useState = useState(0),
	    _useState2 = slicedToArray(_useState, 2),
	    rev = _useState2[0],
	    setRevision = _useState2[1]; // eslint-disable-line


	var _useState3 = useState(function () {
		return FormManager(setRevision, formConfig, data);
	}),
	    _useState4 = slicedToArray(_useState3, 1),
	    formManager = _useState4[0];

	return formManager;
}

var object = PropTypes.object;


useFormManager.propTypes = {
	formConfig: object,
	data: object
};

/**
 * Helper to log form-data using alias-name (if exists) for each field
 *
 * @param {Object} form   		Form-Manager instance object
 * @param {string} [label]      Title in log
 */
function logFormData(form, label) {
	console.log(label || 'FormManager', {
		'form.isDirty()': form.isDirty(),
		'form.getChanges()': form.getChanges(),
		'form.getData()': form.getData(),
		'form.getValues()': form.getValues(),
		'form.getState()': form.getState(),
		'form.getErrors()': form.getErrors()
	});
}

// Class and Hook versions of FormManager
var formatDate$1 = formatters$2.date;

export default FormManager;
export { clone, cloneDeep, defaults$1 as defaults, defaultsDeep, emptyValuesToNull, forOwn, formatDate$1 as formatDate, getObjectValue, inRange, isArray, isBoolean, isDate, isEmpty, isEqual, isFunction, isInteger, isNil, isNumber, isObject, isPlainObject, isRegExp, isSafeInteger, isString, isUndefined, itemToArray, logFormData, merge, omit, parseDate$1 as parseDate, setObjectValue, toInteger$1 as toInteger, toNumber, toString, trim, uniq, useFormManager };



//////////////////
// WEBPACK FOOTER
// ../esm/index.js
// module id = 39
// module chunks = 0